import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../LanguageContext';
import { useAuth } from '../AuthContext';
import { useBrand } from '../BrandContext';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  fetchLeads, fetchLeadStats, fetchSalesTeam,
  createLead, updateLead as apiUpdateLead, patchLeadStatus, deleteLead as apiDeleteLead, addCommunication,
  transitionLeadStatus, fetchLeadTransitions, reopenLead, sendDirective, replyDirective,
  brokerAssignLead, bulkImportLeads, leadProjectsList, toggleLeadImportant,
} from '../api';
import { projLabel } from '../components/UnitsDeals/constants';
import LoadError from '../components/LoadError';
import BrokerAddCustomerModal from '../components/BrokerAddCustomerModal';

// ── Constants ──
const PIPELINE = ['new','contacted','interested','site_visit_scheduled','negotiating','offer_sent','won','lost'];
// snapchat_aqar / snapchat_site_visit are intentionally NOT selectable (removed from the
// dropdowns). Their label maps are kept so leads already tagged with them still display.
const SOURCES = ['snapchat','tiktok','google_ads','instagram','facebook','aqar','site_visitor','whatsapp','website','broker'];
const SOURCE_LABELS = {
  snapchat: { en: 'Snapchat', ar: '\u0633\u0646\u0627\u0628\u0634\u0627\u062A' },
  snapchat_aqar: { en: 'Snapchat - Aqar', ar: '\u0633\u0646\u0627\u0628\u0634\u0627\u062A - \u0639\u0642\u0627\u0631' },
  snapchat_site_visit: { en: 'Snapchat - Site Visit', ar: '\u0633\u0646\u0627\u0628\u0634\u0627\u062A - \u0632\u0627\u0631 \u0627\u0644\u0645\u0634\u0631\u0648\u0639' },
  tiktok: { en: 'TikTok', ar: '\u062A\u064A\u0643\u062A\u0648\u0643' },
  google_ads: { en: 'Google Ads', ar: '\u0625\u0639\u0644\u0627\u0646\u0627\u062A \u062C\u0648\u062C\u0644' },
  instagram: { en: 'Instagram', ar: '\u0627\u0646\u0633\u062A\u063A\u0631\u0627\u0645' },
  facebook: { en: 'Facebook', ar: '\u0641\u064A\u0633\u0628\u0648\u0643' },
  aqar: { en: 'Aqar', ar: '\u0639\u0642\u0627\u0631' },
  site_visitor: { en: 'Site Visitor', ar: '\u0632\u0627\u0631 \u0627\u0644\u0645\u0634\u0631\u0648\u0639' },
  whatsapp: { en: 'WhatsApp', ar: '\u0648\u0627\u062a\u0633\u0627\u0628' },
  website: { en: 'Website', ar: '\u0627\u0644\u0645\u0648\u0642\u0639' },
  broker: { en: 'Broker', ar: '\u0648\u0633\u064a\u0637' },
};
const sourceLabel = (s, lang) => SOURCE_LABELS[s]?.[lang] || SOURCE_LABELS[s]?.en || s.replace('_', ' ');
// No hardcoded tenant projects — the list comes from the tenant's own config
// (BRAND config → brand.projects). Empty until the tenant defines its projects.
const DEFAULT_PROJECTS = [];
let PROJECTS = DEFAULT_PROJECTS;
const STATUS_COLORS = {
  new: '#6366f1', contacted: '#f59e0b', interested: '#8b5cf6',
  site_visit_scheduled: '#06b6d4', site_visit_done: '#f97316',
  offer_sent: '#3b82f6', negotiating: '#ec4899', won: '#22c55e', lost: '#ef4444',
};
const COMM_TYPES = ['call','whatsapp','meeting','note'];
const COMM_ICONS = { call: '\u{1F4DE}', whatsapp: '\u{1F4AC}', meeting: '\u{1F91D}', note: '\u{1F4DD}' };

function genId() { return 'l_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7); }

// ── Sub-components ──

function RoleSwitcher({ role, setRole, currentSales, setCurrentSales, salesTeam, t }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div className="tabs">
        <button className={`tab ${role === 'admin' ? 'active' : ''}`} onClick={() => setRole('admin')}>{t('crmAdmin')}</button>
        <button className={`tab ${role === 'sales' ? 'active' : ''}`} onClick={() => setRole('sales')}>{t('crmSales')}</button>
      </div>
      {role === 'sales' && (
        <select value={currentSales} onChange={e => setCurrentSales(e.target.value)} style={{ width: 'auto', padding: '6px 12px', fontSize: '0.8rem' }}>
          {salesTeam.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
    </div>
  );
}

function LeadStats({ stats, t }) {
  const items = [
    { label: t('crmTotalLeads'), value: stats.total || 0, color: 'var(--text-primary)' },
    { label: t('crmNewLeads'), value: stats.newCount || 0, color: STATUS_COLORS.new },
    { label: t('crmWonDeals'), value: stats.wonCount || 0, color: STATUS_COLORS.won },
    { label: t('crmConversionRate'), value: (stats.conversionRate || 0).toFixed(1) + '%', color: 'var(--success)' },
  ];
  return (
    <div className="crm-stats-bar mb-6">
      {items.map((item, i) => (
        <div key={i} className="crm-stat-item">
          <span className="crm-stat-value" style={{ color: item.color }}>{item.value}</span>
          <span className="crm-stat-label">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

function CampaignMultiSelect({ campaigns, selected, onChange, t }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = React.useRef(null);
  const dropRef = React.useRef(null);

  useEffect(() => {
    const handleClick = e => {
      if (btnRef.current?.contains(e.target) || dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left });
    }
    setOpen(!open);
  };

  const toggle = (c) => {
    const next = selected.includes(c) ? selected.filter(x => x !== c) : [...selected, c];
    onChange(next);
  };

  return (
    <>
      <button ref={btnRef} onClick={handleToggle} style={{
        padding: '6px 12px', fontSize: '0.82rem', border: '1px solid var(--border)',
        borderRadius: 8, background: selected.length > 0 ? 'rgba(99,102,241,0.08)' : 'var(--bg-card)',
        color: selected.length > 0 ? 'var(--accent)' : 'var(--text-primary)',
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
        fontWeight: selected.length > 0 ? 600 : 400,
      }}>
        {selected.length === 0 ? t('crmFilterCampaign') : `${t('crmFilterCampaign')} (${selected.length})`}
        <span style={{ fontSize: '0.6rem' }}>{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div ref={dropRef} style={{
          position: 'fixed', top: pos.top, left: pos.left, zIndex: 99999,
          background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10,
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)', maxHeight: 320, overflowY: 'auto',
          minWidth: 260, padding: '6px 0',
        }}>
          {selected.length > 0 && (
            <div onClick={() => onChange([])} style={{
              padding: '8px 14px', fontSize: '0.78rem', color: 'var(--error)', cursor: 'pointer',
              borderBottom: '1px solid var(--border)', fontWeight: 600,
            }}>
              {t('crmClearFilters')}
            </div>
          )}
          {campaigns.map(c => (
            <label key={c} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
              cursor: 'pointer', fontSize: '0.82rem',
              background: selected.includes(c) ? 'rgba(99,102,241,0.08)' : 'transparent',
            }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.06)'}
              onMouseLeave={e => e.currentTarget.style.background = selected.includes(c) ? 'rgba(99,102,241,0.08)' : 'transparent'}
            >
              <input type="checkbox" checked={selected.includes(c)} onChange={() => toggle(c)}
                style={{ accentColor: 'var(--accent)', width: 16, height: 16 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c}</span>
            </label>
          ))}
          {campaigns.length === 0 && (
            <div style={{ padding: '12px 14px', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{t('noData')}</div>
          )}
        </div>
      )}
    </>
  );
}

function FilterBar({ filters, setFilters, campaigns, salesTeam, brokerCompanies = [], isAdmin, t, lang }) {
  const update = (k, v) => setFilters(p => ({ ...p, [k]: v }));
  return (
    <div className="filter-bar">
      <input
        type="text"
        value={filters.search || ''}
        onChange={e => update('search', e.target.value)}
        placeholder={t('crmSearchPlaceholder')}
        style={{ width: 'auto', minWidth: 180, padding: '6px 12px', fontSize: '0.82rem' }}
      />
      <select value={filters.source} onChange={e => update('source', e.target.value)} style={{ width: 'auto' }}>
        <option value="">{t('crmFilterSource')}</option>
        {SOURCES.map(s => <option key={s} value={s}>{sourceLabel(s, lang)}</option>)}
        <option value="broker">{lang === 'ar' ? 'وسيط' : 'Broker'}</option>
      </select>
      {isAdmin && brokerCompanies.length > 0 && (
        <select value={filters.broker || ''} onChange={e => update('broker', e.target.value)} style={{ width: 'auto' }}>
          <option value="">{lang === 'ar' ? 'كل الوسطاء' : 'All brokers'}</option>
          {brokerCompanies.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
      )}
      <CampaignMultiSelect
        campaigns={campaigns}
        selected={filters.campaigns || []}
        onChange={v => update('campaigns', v)}
        t={t}
      />
      <select value={filters.status} onChange={e => update('status', e.target.value)} style={{ width: 'auto' }}>
        <option value="">{t('crmFilterStatus')}</option>
        {PIPELINE.map(s => <option key={s} value={s}>{t('crmStatus_' + s)}</option>)}
      </select>
      <select value={filters.project || ''} onChange={e => update('project', e.target.value)} style={{ width: 'auto' }}>
        <option value="">{t('crmProject')}</option>
        {PROJECTS.map(p => <option key={p} value={p}>{projLabel(p, t)}</option>)}
      </select>
      {isAdmin && (
        <select value={filters.assigned} onChange={e => update('assigned', e.target.value)} style={{ width: 'auto' }}>
          <option value="">{t('crmFilterAssigned')}</option>
          {salesTeam.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
      <select value={filters.workedOn} onChange={e => update('workedOn', e.target.value)} style={{ width: 'auto' }}>
        <option value="">{t('crmFilterWorkedOn')}</option>
        <option value="today">{t('crmFilterToday')}</option>
        <option value="yesterday">{t('crmFilterYesterday')}</option>
        <option value="custom">{t('crmFilterCustomDate')}</option>
      </select>
      {filters.workedOn === 'custom' && (
        <>
          <input type="date" value={filters.dateFrom} onChange={e => update('dateFrom', e.target.value)} style={{ width: 'auto', padding: '6px 10px' }} />
          <input type="date" value={filters.dateTo} onChange={e => update('dateTo', e.target.value)} style={{ width: 'auto', padding: '6px 10px' }} />
        </>
      )}
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
        {t('crmCreatedFrom')}
        <input type="date" value={filters.createdFrom} onChange={e => update('createdFrom', e.target.value)} style={{ width: 'auto', padding: '6px 10px' }} />
      </label>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
        {t('crmCreatedTo')}
        <input type="date" value={filters.createdTo} onChange={e => update('createdTo', e.target.value)} style={{ width: 'auto', padding: '6px 10px' }} />
      </label>
      <button
        className={`btn btn-sm ${filters.important === '1' ? 'btn-primary' : 'btn-secondary'}`}
        onClick={() => update('important', filters.important === '1' ? '' : '1')}
        title={lang === 'ar' ? 'عرض العملاء المهمين فقط' : 'Show important leads only'}>
        {filters.important === '1' ? '★' : '☆'} {lang === 'ar' ? 'المهمة' : 'Important'}
      </button>
      {Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : Boolean(v)) && (
        <button className="btn btn-secondary btn-sm" onClick={() => setFilters({ source: '', campaigns: [], status: '', assigned: '', broker: '', dateFrom: '', dateTo: '', createdFrom: '', createdTo: '', search: '', workedOn: '', project: '', important: '' })}>
          {t('crmClearFilters')}
        </button>
      )}
    </div>
  );
}

function StatusBadge({ status, t }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px',
      borderRadius: 20, fontSize: '0.72rem', fontWeight: 600,
      background: (STATUS_COLORS[status] || '#888') + '22', color: STATUS_COLORS[status] || '#888',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLORS[status] || '#888' }} />
      {t('crmStatus_' + status)}
    </span>
  );
}

function SourceBadge({ source, brokerCompany }) {
  const badges = {
    broker: { bg: 'rgba(124, 58, 237, 0.15)', color: '#7C3AED', text: 'وسيط' },
    snapchat: { bg: 'rgba(180, 150, 0, 0.15)', color: '#b09600', text: 'Snapchat' },
    snapchat_aqar: { bg: 'rgba(180, 150, 0, 0.15)', color: '#b09600', text: 'Snap-\u0639\u0642\u0627\u0631' },
    snapchat_site_visit: { bg: 'rgba(180, 150, 0, 0.15)', color: '#b09600', text: 'Snap-\u0632\u064A\u0627\u0631\u0629' },
    tiktok: { bg: 'rgba(238, 29, 82, 0.15)', color: '#EE1D52', text: 'TikTok' },
    google_ads: { bg: 'rgba(66, 133, 244, 0.15)', color: '#4285F4', text: 'Google' },
    instagram: { bg: 'rgba(225, 48, 108, 0.15)', color: '#E1306C', text: 'Instagram' },
    facebook: { bg: 'rgba(24, 119, 242, 0.15)', color: '#1877F2', text: 'Facebook' },
    aqar: { bg: 'rgba(139, 69, 19, 0.15)', color: '#8B4513', text: '\u0639\u0642\u0627\u0631' },
    site_visitor: { bg: 'rgba(16, 185, 129, 0.15)', color: '#10B981', text: '\u0632\u0627\u0631 \u0627\u0644\u0645\u0634\u0631\u0648\u0639' },
    whatsapp: { bg: 'rgba(37, 211, 102, 0.15)', color: '#25D366', text: '\u0648\u0627\u062a\u0633\u0627\u0628' },
    website: { bg: 'rgba(14, 165, 233, 0.15)', color: '#0EA5E9', text: '\u0627\u0644\u0645\u0648\u0642\u0639' },
  };
  const b = badges[source] || { bg: 'rgba(136,136,136,0.15)', color: '#888', text: source || '' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      <span style={{
        padding: '3px 9px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 700,
        background: b.bg, color: b.color,
      }}>
        {b.text}
      </span>
      {source === 'broker' && brokerCompany && (
        <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#7C3AED' }}>{brokerCompany}</span>
      )}
    </span>
  );
}

// Broker performance scoreboard — per-broker lead volume, pending, and conversion.
// Click a row to filter the leads list to that broker.
function BrokerMonitor({ brokers, onPick, lang }) {
  if (!brokers.length) return null;
  const tot = brokers.reduce((a, b) => ({ total: a.total + b.total, pending: a.pending + b.pending, won: a.won + b.won, assigned: a.assigned + b.assigned }), { total: 0, pending: 0, won: 0, assigned: 0 });
  const th = { textAlign: lang === 'ar' ? 'right' : 'left', padding: '6px 10px', fontSize: '0.68rem', color: 'var(--text-secondary)', fontWeight: 700, whiteSpace: 'nowrap' };
  const td = { padding: '6px 10px', fontSize: '0.8rem', whiteSpace: 'nowrap', textAlign: lang === 'ar' ? 'right' : 'left' };
  const cols = [
    ['company', lang === 'ar' ? 'الوسيط' : 'Broker'], ['total', lang === 'ar' ? 'العملاء' : 'Leads'],
    ['pending', lang === 'ar' ? 'قيد الاعتماد' : 'Pending'], ['assigned', lang === 'ar' ? 'مُسند' : 'Assigned'],
    ['interested', lang === 'ar' ? 'مهتم' : 'Interested'], ['visit', lang === 'ar' ? 'زيارة' : 'Visit'],
    ['won', lang === 'ar' ? 'مكسوب' : 'Won'], ['conv', lang === 'ar' ? 'التحويل' : 'Conv.'],
  ];
  return (
    <div className="card" style={{ padding: 12, marginBottom: 12, overflowX: 'auto' }}>
      <div style={{ fontSize: '0.85rem', fontWeight: 800, marginBottom: 8 }}>
        {lang === 'ar' ? 'أداء الوسطاء' : 'Broker performance'} · {tot.total} {lang === 'ar' ? 'عميل' : 'leads'}
        {tot.pending > 0 && <span style={{ color: '#f59e0b' }}> · {tot.pending} {lang === 'ar' ? 'قيد الاعتماد' : 'pending'}</span>}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr style={{ borderBottom: '1px solid var(--border)' }}>{cols.map(c => <th key={c[0]} style={th}>{c[1]}</th>)}<th style={th}></th></tr></thead>
        <tbody>
          {brokers.map(b => (
            <tr key={b.company} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => onPick(b.company)}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-primary)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              <td style={{ ...td, fontWeight: 700, color: '#7C3AED' }}>{b.company}</td>
              <td style={td}>{b.total}</td>
              <td style={{ ...td, color: b.pending ? '#f59e0b' : 'inherit', fontWeight: b.pending ? 700 : 400 }}>{b.pending}</td>
              <td style={td}>{b.assigned}</td>
              <td style={td}>{b.interested}</td>
              <td style={td}>{b.visit}</td>
              <td style={{ ...td, color: b.won ? '#22c55e' : 'inherit', fontWeight: b.won ? 700 : 400 }}>{b.won}</td>
              <td style={td}>{b.conversionRate.toFixed(1)}%</td>
              <td style={{ ...td, color: 'var(--accent)', fontSize: '0.72rem', fontWeight: 700 }}>{lang === 'ar' ? 'عرض' : 'View'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KanbanBoard({ leads, onMoveStatus, onLeadClick, t, total, page, onPageChange, pageSize }) {
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  return (
    <>
    <div className="crm-kanban">
      {PIPELINE.map(status => {
        const col = leads.filter(l => l.status === status);
        return (
          <div key={status}
            className={`crm-kanban-column ${dragOver === status ? 'drag-over' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragOver(status); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={() => { if (dragId) { onMoveStatus(dragId, status); setDragId(null); setDragOver(null); } }}
          >
            <div className="crm-kanban-column-header">
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[status], flexShrink: 0 }} />
              <span style={{ flex: 1, fontWeight: 600, fontSize: '0.8rem' }}>{t('crmStatus_' + status)}</span>
              <span style={{ fontSize: '0.72rem', background: 'var(--bg-primary)', padding: '2px 8px', borderRadius: 10, color: 'var(--text-secondary)', fontWeight: 600 }}>{col.length}</span>
            </div>
            <div className="crm-kanban-column-body">
              {col.map(lead => (
                <div key={lead.id} className="crm-kanban-card"
                  draggable onDragStart={() => setDragId(lead.id)}
                  onClick={() => onLeadClick(lead)}
                >
                  <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 6 }}>{lead.name}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 6, direction: 'ltr' }}>{lead.phone}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <SourceBadge source={lead.source} brokerCompany={lead.brokerCompany} />
                    {lead.assignedTo && <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{lead.assignedTo}</span>}
                  </div>
                  {lead.campaign && <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.campaign}</div>}
                  {lead.project && <div style={{ fontSize: '0.68rem', color: '#8b5cf6', fontWeight: 600, marginTop: lead.campaign ? 2 : 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{projLabel(lead.project, t)}</div>}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
    <Pager total={total} page={page} onPageChange={onPageChange} pageSize={pageSize} shown={leads.length} t={t} />
    </>
  );
}

// Shared pagination footer for both the table and kanban lead views. `total` is
// the full server-side filtered count; `shown` falls back to the current page's
// row count until total loads. Renders the page buttons only when there's more
// than one page.
function Pager({ total, page, onPageChange, pageSize, shown, t }) {
  const count = total || shown;
  const totalPages = Math.ceil(count / pageSize);
  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
        {t('crmTotalLeads')}: <span style={{ color: 'var(--accent)', fontWeight: 800 }}>{count}</span>
      </span>
      {totalPages > 1 && (
        <div className="pagination" style={{ border: 'none', padding: 0 }}>
          <span className="pagination-info">{page * pageSize + 1}–{Math.min((page + 1) * pageSize, count)} / {count}</span>
          <button disabled={page === 0} onClick={() => onPageChange(page - 1)}>{t('prev')}</button>
          {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
            const pn = totalPages <= 5 ? i : page < 3 ? i : page > totalPages - 4 ? totalPages - 5 + i : page - 2 + i;
            return <button key={pn} className={page === pn ? 'active' : ''} onClick={() => onPageChange(pn)}>{pn + 1}</button>;
          })}
          <button disabled={page >= totalPages - 1} onClick={() => onPageChange(page + 1)}>{t('next')}</button>
        </div>
      )}
    </div>
  );
}

function TableView({ leads, onLeadClick, canSelect, selectedIds, onToggleSelect, onSelectAll, onToggleImportant, t, total, page, onPageChange, pageSize }) {
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Build duplicate phone set — phones that appear more than once
  const duplicatePhones = useMemo(() => {
    const phoneCounts = {};
    leads.forEach(l => {
      const p = (l.phone || '').replace(/\s+/g, '');
      if (p) phoneCounts[p] = (phoneCounts[p] || 0) + 1;
    });
    return new Set(Object.keys(phoneCounts).filter(p => phoneCounts[p] > 1));
  }, [leads]);

  const sorted = useMemo(() => {
    return [...leads].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [leads, sortKey, sortDir]);

  const paged = sorted; // Already paginated server-side
  const toggleSort = k => { if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(k); setSortDir('asc'); } };
  const sortArrow = k => sortKey === k ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : '';

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="data-table-wrapper" style={{ maxHeight: 'calc(100vh - 340px)', overflowY: 'auto' }}>
        <table className="data-table resp-cards" style={{ fontSize: '0.82rem' }}>
          <thead>
            <tr>
              {canSelect && (
                <th style={{ position: 'sticky', top: 0, width: 36, textAlign: 'center' }}>
                  <input type="checkbox" checked={paged.length > 0 && paged.every(l => selectedIds.has(l.id))}
                    onChange={e => onSelectAll(paged.map(l => l.id), e.target.checked)} />
                </th>
              )}
              <th onClick={() => toggleSort('name')} style={{ position: 'sticky', top: 0 }}>{t('crmLeadName')}{sortArrow('name')}</th>
              <th onClick={() => toggleSort('phone')} style={{ position: 'sticky', top: 0 }}>{t('crmPhone')}{sortArrow('phone')}</th>
              <th onClick={() => toggleSort('source')} style={{ position: 'sticky', top: 0 }}>{t('crmSource')}{sortArrow('source')}</th>
              <th onClick={() => toggleSort('status')} style={{ position: 'sticky', top: 0 }}>{t('crmStatusLabel')}{sortArrow('status')}</th>
              <th onClick={() => toggleSort('assignedTo')} style={{ position: 'sticky', top: 0 }}>{t('crmAssignedTo')}{sortArrow('assignedTo')}</th>
              <th onClick={() => toggleSort('campaign')} style={{ position: 'sticky', top: 0 }}>{t('crmCampaign')}{sortArrow('campaign')}</th>
              <th onClick={() => toggleSort('project')} style={{ position: 'sticky', top: 0 }}>{t('crmProject')}{sortArrow('project')}</th>
              <th onClick={() => toggleSort('createdAt')} style={{ position: 'sticky', top: 0 }}>{t('crmCreatedAt')}{sortArrow('createdAt')}</th>
              <th onClick={() => toggleSort('updatedAt')} style={{ position: 'sticky', top: 0 }}>{t('crmLastUpdate')}{sortArrow('updatedAt')}</th>
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr><td colSpan={canSelect ? 10 : 9} style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>{t('noData')}</td></tr>
            ) : paged.map(lead => (
              <tr key={lead.id} onClick={() => onLeadClick(lead)} style={{ cursor: 'pointer', background: selectedIds?.has(lead.id) ? 'rgba(99,102,241,0.06)' : undefined }}>
                {canSelect && (
                  <td style={{ textAlign: 'center', width: 36 }} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedIds.has(lead.id)} onChange={() => onToggleSelect(lead.id)} />
                  </td>
                )}
                <td data-label={t('crmLeadName')} style={{ fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {onToggleImportant && (
                    <span
                      onClick={e => { e.stopPropagation(); onToggleImportant(lead); }}
                      title={lead.isImportant ? 'Unmark important' : 'Mark as important'}
                      style={{ cursor: 'pointer', marginInlineEnd: 5, fontSize: '0.95rem', color: lead.isImportant ? '#f5a623' : 'var(--text-secondary)', opacity: lead.isImportant ? 1 : 0.45 }}
                    >{lead.isImportant ? '★' : '☆'}</span>
                  )}
                  {lead.priority ? <span title="Priority" style={{ marginInlineEnd: 4 }}>🚩</span> : null}{lead.name}</td>
                <td data-label={t('crmPhone')} style={{ direction: 'ltr', fontSize: '0.78rem', color: duplicatePhones.has((lead.phone || '').replace(/\s+/g, '')) ? 'var(--error)' : undefined, fontWeight: duplicatePhones.has((lead.phone || '').replace(/\s+/g, '')) ? 700 : undefined }}>{lead.phone}{duplicatePhones.has((lead.phone || '').replace(/\s+/g, '')) && <span style={{ fontSize: '0.65rem', marginLeft: 4 }} title="Duplicate">⚠</span>}</td>
                <td data-label={t('crmSource')}><SourceBadge source={lead.source} brokerCompany={lead.brokerCompany} /></td>
                <td data-label={t('crmStatusLabel')}><StatusBadge status={lead.status} t={t} /></td>
                <td data-label={t('crmAssignedTo')} style={{ fontSize: '0.78rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.assignedTo || '\u2014'}</td>
                <td data-label={t('crmCampaign')} style={{ fontSize: '0.78rem', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.campaign || '\u2014'}</td>
                <td data-label={t('crmProject')} style={{ fontSize: '0.75rem', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: lead.project ? '#8b5cf6' : 'var(--text-secondary)', fontWeight: lead.project ? 600 : 400 }}>{lead.project ? projLabel(lead.project, t) : '\u2014'}</td>
                <td data-label={t('crmCreatedAt')} style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{new Date(lead.createdAt).toLocaleDateString()} {new Date(lead.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                <td data-label={t('crmLastUpdate')} style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{lead.updatedAt ? (<>{new Date(lead.updatedAt).toLocaleDateString()} {new Date(lead.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>) : '\u2014'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager total={total} page={page} onPageChange={onPageChange} pageSize={pageSize} shown={sorted.length} t={t} />
    </div>
  );
}

function LeadModal({ lead, onClose, onSave, onDelete, onAddComm, onTransition, onReopen, onDirective, onReply, onBrokerAssign, onToggleImportant, isBrokerOwner = false, salesTeam = [], brokerCompanies = [], role, t, lang }) {
  const [form, setForm] = useState({ ...lead });
  useEffect(() => {
    const handleEsc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [transitions, setTransitions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const [adCreative, setAdCreative] = useState(null);
  const [comms, setComms] = useState([]);
  const [relatedHistory, setRelatedHistory] = useState([]);
  // Genuine communications only: every status change auto-logs a 'note' carrying a
  // hidden <!--meta--> marker. Keep the entry when the rep actually wrote a note (so
  // everything a sales rep writes is visible), and drop only the note-less auto-logs.
  // Newest first (top → bottom).
  const visibleComms = [...comms]
    .filter(c => (c.message || '').replace(/\n?<!--meta:[\s\S]*?-->/g, '').trim())
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  const [directiveText, setDirectiveText] = useState('');
  const [directiveAssignee, setDirectiveAssignee] = useState('');
  const [replyText, setReplyText] = useState('');
  const [busyDir, setBusyDir] = useState(false);
  const [brokerAssigning, setBrokerAssigning] = useState(false);
  useEffect(() => {
    fetchLeadTransitions(lead.id).then(data => { setTransitions(data || []); setLoadingHistory(false); });
    // Fetch full lead (list endpoint strips notes + communications)
    axios.get(`/api/leads/${lead.id}`).then(r => {
      const full = r.data?.lead;
      if (!full) return;
      if (full.adCreative) setAdCreative(full.adCreative);
      setComms(full.communications || []);
      setRelatedHistory(full.relatedHistory || []);
      setForm(p => ({ ...p, notes: full.notes != null ? full.notes : p.notes }));
    }).catch(() => {});
  }, [lead.id]);

  // Status update state
  const [selectedOutcome, setSelectedOutcome] = useState(null); // 'interested' | 'not_answering' | 'not_interested'
  const [outcomeData, setOutcomeData] = useState({
    statusNote: '', project: lead.project || '', paymentMethod: '', siteVisit: null,
    siteVisitDate: '', followUpDate: '', noVisitReason: '', lossReason: '', lossDetails: '',
    // Site-visit outcome flow (when the lead is already site_visit_scheduled):
    visitOpinion: null,     // 'interested' | 'not_interested' (after a visit happened)
    visitNextAction: null,  // 'reschedule' | 'followup' (after a no-show)
  });
  const [outcomeErrors, setOutcomeErrors] = useState({});
  const ud = (k, v) => { setOutcomeData(p => ({ ...p, [k]: v })); setOutcomeErrors(p => ({ ...p, [k]: false })); };

  // For sales reps and managers marking a lead "not interested", التفاصيل (loss
  // details) already captures the context — skip the redundant required ملاحظات note.
  const { user, can } = useAuth();
  const skipStatusNote = (user?.role === 'sales' || user?.role === 'manager') && selectedOutcome === 'not_interested';

  const canDirective = can('leads.directive');
  const hasDirective = comms.some(c => c.type === 'directive');
  const sendDirectiveNow = async () => {
    if (!directiveText.trim() || busyDir) return;
    setBusyDir(true);
    const res = await onDirective(lead.id, directiveText.trim(), directiveAssignee);
    setBusyDir(false);
    if (res?.error) return;
    if (res?.lead?.communications) setComms(res.lead.communications);
    setDirectiveText(''); setDirectiveAssignee('');
  };
  const sendReplyNow = async () => {
    if (!replyText.trim() || busyDir) return;
    setBusyDir(true);
    const res = await onReply(lead.id, replyText.trim());
    setBusyDir(false);
    if (res?.error) return;
    if (res?.lead?.communications) setComms(res.lead.communications);
    setReplyText('');
  };

  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Check if there's a pending follow-up
  const hasFollowUp = lead.followUpDate && new Date(lead.followUpDate) <= new Date();
  const hasFutureFollowUp = lead.followUpDate && new Date(lead.followUpDate) > new Date();

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  // Validate and submit the outcome
  const handleOutcomeSubmit = async () => {
    const errs = {};
    if (!skipStatusNote && !(outcomeData.statusNote || '').trim()) errs.statusNote = true;
    if (selectedOutcome === 'interested') {
      if (!outcomeData.project) errs.project = true;
      if (outcomeData.siteVisit === null) errs.siteVisit = true;
      if (outcomeData.siteVisit === true && !outcomeData.siteVisitDate) errs.siteVisitDate = true;
      if (outcomeData.siteVisit === false && !(outcomeData.noVisitReason || '').trim()) errs.noVisitReason = true;
      if (outcomeData.siteVisit === false && !outcomeData.followUpDate) errs.followUpDate = true;
    }
    if (selectedOutcome === 'not_answering' && !outcomeData.followUpDate) errs.followUpDate = true;
    if (selectedOutcome === 'not_interested' && !outcomeData.lossReason) errs.lossReason = true;
    if (selectedOutcome === 'not_interested' && !(outcomeData.lossDetails || '').trim()) errs.lossDetails = true;
    // Site-visit flow: the visit happened → opinion; or a no-show → reason + next action.
    if (selectedOutcome === 'visited') {
      if (outcomeData.visitOpinion === null) errs.visitOpinion = true;
      if (outcomeData.visitOpinion === 'interested' && !outcomeData.followUpDate) errs.followUpDate = true;
      if (outcomeData.visitOpinion === 'not_interested' && !outcomeData.lossReason) errs.lossReason = true;
      if (outcomeData.visitOpinion === 'not_interested' && !(outcomeData.lossDetails || '').trim()) errs.lossDetails = true;
    }
    if (selectedOutcome === 'no_show') {
      if (!(outcomeData.noVisitReason || '').trim()) errs.noVisitReason = true;
      if (outcomeData.visitNextAction === null) errs.visitNextAction = true;
      if (outcomeData.visitNextAction === 'reschedule' && !outcomeData.siteVisitDate) errs.siteVisitDate = true;
      if (outcomeData.visitNextAction === 'followup' && !outcomeData.followUpDate) errs.followUpDate = true;
    }
    setOutcomeErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    const payload = { outcome: selectedOutcome, doneBy: form.assignedTo || 'Admin', statusNote: outcomeData.statusNote };

    if (selectedOutcome === 'interested') {
      payload.toStatus = outcomeData.siteVisit === true ? 'site_visit_scheduled' : 'interested';
      payload.project = outcomeData.project;
      if (outcomeData.paymentMethod) payload.paymentMethod = outcomeData.paymentMethod;
      if (outcomeData.siteVisit === true) payload.siteVisitDate = outcomeData.siteVisitDate;
      if (outcomeData.siteVisit === false) {
        payload.followUpDate = outcomeData.followUpDate;
        if (outcomeData.noVisitReason) payload.noVisitReason = outcomeData.noVisitReason;
      }
    } else if (selectedOutcome === 'not_answering') {
      payload.toStatus = 'contacted';
      payload.followUpDate = outcomeData.followUpDate;
    } else if (selectedOutcome === 'not_interested') {
      payload.toStatus = 'lost';
      payload.lossReason = outcomeData.lossReason;
      if (outcomeData.lossDetails) payload.lossDetails = outcomeData.lossDetails;
    } else if (selectedOutcome === 'visited') {
      if (outcomeData.visitOpinion === 'interested') {
        payload.toStatus = 'site_visit_done';
        payload.followUpDate = outcomeData.followUpDate;
      } else {
        payload.toStatus = 'lost';
        payload.lossReason = outcomeData.lossReason;
        if (outcomeData.lossDetails) payload.lossDetails = outcomeData.lossDetails;
      }
    } else if (selectedOutcome === 'no_show') {
      payload.noVisitReason = outcomeData.noVisitReason;
      if (outcomeData.visitNextAction === 'reschedule') {
        payload.toStatus = 'site_visit_scheduled';
        payload.siteVisitDate = outcomeData.siteVisitDate;
      } else {
        payload.toStatus = 'interested';
        payload.followUpDate = outcomeData.followUpDate;
      }
    }

    onTransition(lead.id, payload);
  };

  const PAYMENT_METHODS = ['cash','bank'];
  const LOSS_REASONS = ['crmLossPrice','crmLossNoResponse','crmLossCompetitor','crmLossNotInterested','crmLossTiming','crmLossLocation','crmLossOther'];

  // Don't show outcome cards for terminal statuses
  const showOutcomeCards = form.status !== 'won' && form.status !== 'lost';
  // When a visit is already scheduled, the outcome cards become the visit flow
  // ("did the customer visit?") instead of the contacted flow.
  const isVisitFlow = form.status === 'site_visit_scheduled';

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal" onClick={e => e.stopPropagation()}>
        <div className="crm-modal-header">
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            {onToggleImportant && (
              <span
                onClick={() => onToggleImportant(lead)}
                title={lead.isImportant ? (lang === 'ar' ? 'إلغاء التمييز كعميل مهم' : 'Unmark important') : (lang === 'ar' ? 'تمييز كعميل مهم (إمكانية عالية)' : 'Mark as important (high potential)')}
                style={{ cursor: 'pointer', fontSize: '1.25rem', lineHeight: 1, color: lead.isImportant ? '#f5a623' : 'var(--text-secondary)', opacity: lead.isImportant ? 1 : 0.5 }}
              >{lead.isImportant ? '★' : '☆'}</span>
            )}
            {t('crmLeadDetails')}
            {(form.priority || lead.priority) ? (
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#fff', background: '#ef4444', borderRadius: 6, padding: '2px 8px' }}>
                🚩 {lang === 'ar' ? 'أولوية' : 'PRIORITY'}
              </span>
            ) : null}
          </h2>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>&times;</button>
        </div>

        {/* Follow-up reminder banner */}
        {hasFollowUp && (
          <div style={{
            background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 12,
            display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#d97706',
          }}>
            <span style={{ fontSize: '1.2rem' }}>&#9200;</span>
            <div>
              <strong>{t('wizFollowUpDue')}</strong> — {new Date(lead.followUpDate).toLocaleString()}
              <div style={{ fontSize: '0.78rem', marginTop: 2 }}>{t('wizFollowUpDueDesc')}</div>
            </div>
          </div>
        )}
        {hasFutureFollowUp && (
          <div style={{
            background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 12,
            display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: 'var(--accent)',
          }}>
            <span style={{ fontSize: '1.2rem' }}>&#128197;</span>
            <div>
              <strong>{t('wizFollowUpScheduled')}</strong> — {new Date(lead.followUpDate).toLocaleString()}
            </div>
          </div>
        )}

        {/* Ad Creative */}
        {adCreative && adCreative.thumbnail_url && (
          <div style={{
            background: 'var(--bg-primary)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 12,
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <img src={adCreative.thumbnail_url} alt="Ad Creative" style={{ width: 70, height: 70, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--border)' }} onError={e => { e.target.parentElement.style.display = 'none'; }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 2 }}>{t('crmAdCreative') || 'Ad Creative'}</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{adCreative.ad_name || lead.campaign}</div>
              {adCreative.campaign_name && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>{adCreative.campaign_name}</div>}
              {adCreative.creative_headline && <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 2 }}>{adCreative.creative_headline}</div>}
            </div>
          </div>
        )}

        {/* Lead info */}
        <div className="crm-form-grid">
          <div className="input-group"><label>{t('crmLeadName')}</label><input value={form.name} onChange={e => u('name', e.target.value)} readOnly={!can('leads.edit')} style={!can('leads.edit') ? { opacity: 0.7, cursor: 'default' } : {}} /></div>
          <div className="input-group"><label>{t('crmPhone')}</label><input value={form.phone} onChange={e => u('phone', e.target.value)} readOnly={role !== 'admin'} style={{ direction: 'ltr', ...(role !== 'admin' ? { opacity: 0.7, cursor: 'default' } : {}) }} /></div>
          <div className="input-group"><label>{t('crmEmail')}</label><input value={form.email || ''} onChange={e => u('email', e.target.value)} readOnly={role !== 'admin'} style={{ direction: 'ltr', ...(role !== 'admin' ? { opacity: 0.7, cursor: 'default' } : {}) }} /></div>
          <div className="input-group"><label>{t('crmSource')}</label>
            {role === 'admin' ? (
              <select value={form.source} onChange={e => u('source', e.target.value)}>{SOURCES.map(s => <option key={s} value={s}>{sourceLabel(s, lang)}</option>)}</select>
            ) : (
              <input value={sourceLabel(form.source || '', lang)} readOnly style={{ opacity: 0.7, cursor: 'default' }} />
            )}
          </div>
          <div className="input-group"><label>{t('crmCampaign')}</label><input value={form.campaign || ''} onChange={e => u('campaign', e.target.value)} readOnly={role !== 'admin'} style={role !== 'admin' ? { opacity: 0.7, cursor: 'default' } : {}} /></div>
          <div className="input-group"><label>{t('crmBudget')}</label><input type="number" value={form.budget || ''} onChange={e => u('budget', Number(e.target.value) || 0)} readOnly={role !== 'admin'} style={role !== 'admin' ? { opacity: 0.7, cursor: 'default' } : {}} /></div>
          {form.project && (
            <div className="input-group"><label>{t('crmProject')}</label>
              <input value={projLabel(form.project, t)} readOnly style={{ opacity: 0.7, cursor: 'default', color: '#8b5cf6', fontWeight: 600 }} />
            </div>
          )}
          <div className="input-group"><label>{t('crmStatusLabel')}</label>
            <div><StatusBadge status={form.status} t={t} /></div>
          </div>
          {lead.createdBy && (
            <div className="input-group"><label>{t('crmCreatedBy')}</label>
              <input value={lead.createdBy} readOnly style={{ opacity: 0.7, cursor: 'default' }} />
            </div>
          )}
          {role === 'admin' && (
            <div className="input-group"><label>{t('crmAssignedTo')}</label>
              <select
                value={form.brokerCompany ? 'broker:' + form.brokerCompany : (form.assignedTo ? 'emp:' + form.assignedTo : '')}
                onChange={e => {
                  const v = e.target.value;
                  if (v.startsWith('broker:')) setForm(p => ({ ...p, brokerCompany: v.slice(7), assignedTo: '', source: 'broker' }));
                  else if (v.startsWith('emp:')) setForm(p => ({ ...p, assignedTo: v.slice(4), brokerCompany: '' }));
                  else setForm(p => ({ ...p, assignedTo: '', brokerCompany: '' }));
                }}>
                <option value="">{'\u2014'}</option>
                <optgroup label={lang === 'ar' ? '\u0627\u0644\u0645\u0648\u0638\u0641\u0648\u0646' : 'Employees'}>
                  {SALES_TEAM_PLACEHOLDER.map(s => <option key={s} value={'emp:' + s}>{s}</option>)}
                </optgroup>
                {brokerCompanies.length > 0 && (
                  <optgroup label={lang === 'ar' ? '\u0627\u0644\u0648\u0633\u0637\u0627\u0621' : 'Brokers'}>
                    {brokerCompanies.map(b => <option key={b} value={'broker:' + b}>{b}</option>)}
                  </optgroup>
                )}
              </select>
              {form.brokerCompany && <div style={{ fontSize: '0.68rem', color: '#7C3AED', marginTop: 3 }}>{lang === 'ar' ? '\u0645\u062d\u0648\u064e\u0651\u0644 \u0625\u0644\u0649 \u0627\u0644\u0648\u0633\u064a\u0637 \u0644\u064a\u0648\u0632\u0651\u0639\u0647 \u0639\u0644\u0649 \u0641\u0631\u064a\u0642\u0647' : 'Handed to the broker to distribute'}</div>}
            </div>
          )}
          {isBrokerOwner && (
            <div className="input-group"><label>{t('crmAssignedTo')}</label>
              <select
                value={form.assignedTo || ''}
                disabled={brokerAssigning}
                onChange={async (e) => {
                  const next = e.target.value;
                  if (!next || next === form.assignedTo) return;
                  setBrokerAssigning(true);
                  const res = await onBrokerAssign(lead.id, next);
                  // On success the parent closes the modal and reloads the list.
                  if (res?.error) { setBrokerAssigning(false); alert(lang === 'ar' ? '\u062a\u0639\u0630\u0651\u0631 \u0627\u0644\u0625\u0633\u0646\u0627\u062f' : 'Could not assign'); }
                }}
              >
                <option value="">{lang === 'ar' ? '\u0627\u062e\u062a\u0631 \u0645\u0648\u0638\u0641\u0627\u064b' : 'Select employee'}</option>
                {salesTeam.map((s) => { const nm = s.name || s; return <option key={nm} value={nm}>{nm}</option>; })}
              </select>
            </div>
          )}
          <div className="input-group" style={{ gridColumn: '1 / -1' }}>
            <label>{t('crmNotes')}</label><textarea rows={2} value={form.notes || ''} onChange={e => u('notes', e.target.value)} />
          </div>
        </div>

        {/* ── Previous records for this customer (same phone) — internal-only; the
              server never sends relatedHistory to broker viewers ── */}
        {Array.isArray(relatedHistory) && relatedHistory.length > 0 && (
          <div style={{ marginTop: 12, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#b45309', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>🔗</span>{lang === 'ar' ? 'سجل سابق لنفس العميل (رقم مكرر)' : 'Previous records for this customer (same phone)'}
            </div>
            {relatedHistory.map((rel, ri) => (
              <div key={rel.id || ri} style={{ marginBottom: ri < relatedHistory.length - 1 ? 10 : 0, paddingBottom: ri < relatedHistory.length - 1 ? 10 : 0, borderBottom: ri < relatedHistory.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 5 }}>
                  <strong style={{ fontSize: '0.82rem' }}>{rel.name}</strong>
                  <span style={{ fontSize: '0.7rem', padding: '1px 7px', borderRadius: 6, background: 'var(--bg-secondary)' }}>{sourceLabel(rel.source || '', lang)}</span>
                  <StatusBadge status={rel.status} t={t} />
                  {rel.assignedTo && <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>· {rel.assignedTo}</span>}
                </div>
                {(rel.transitions || []).map((tr, i) => {
                  let data = {}; try { data = typeof tr.data === 'string' ? JSON.parse(tr.data) : (tr.data || {}); } catch { /* ignore */ }
                  return (
                    <div key={'t' + i} style={{ fontSize: '0.76rem', marginBottom: 3 }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{t('crmStatus_' + tr.from_status)} → </span>
                      <strong>{t('crmStatus_' + tr.to_status)}</strong>
                      <span style={{ fontSize: '0.66rem', color: 'var(--text-secondary)' }}>{tr.done_by ? ` · ${tr.done_by}` : ''}</span>
                      {(data.statusNote || data.lossDetails) && <div style={{ color: 'var(--text-secondary)', marginTop: 1 }}>{data.statusNote || data.lossDetails}</div>}
                    </div>
                  );
                })}
                {(rel.communications || []).map((c, i) => {
                  const m = (c.message || '').replace(/\n?<!--meta:[\s\S]*?-->/g, '').trim();
                  return m ? <div key={'c' + i} style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', marginBottom: 2 }}>💬 {c.by ? <strong>{c.by}: </strong> : ''}{m}</div> : null;
                })}
              </div>
            ))}
          </div>
        )}

        {/* ── Loss Reason Details (shown when lead has a loss reason) ── */}
        {lead.lossReason && (
          <div style={{
            background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: 10, padding: '12px 16px', marginTop: 12,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: lead.lossDetails ? 6 : 0 }}>
              <span style={{ fontSize: '1rem' }}>&#10060;</span>
              <span style={{ fontSize: '0.85rem', color: '#ef4444', fontWeight: 600 }}>{t('crmLossReason')}:</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>{lead.lossReason}</span>
            </div>
            {lead.lossDetails && (
              <div style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', paddingLeft: 28 }}>
                {lead.lossDetails}
              </div>
            )}
          </div>
        )}

        {/* ── Reopen a closed (won/lost) lead ── */}
        {!showOutcomeCards && onReopen && can('leads.edit') && (
          <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 10, background: 'var(--bg-primary)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              {form.status === 'won'
                ? (lang === 'ar' ? 'هذه الصفقة مغلقة (مكتملة).' : 'This deal is closed (won).')
                : (lang === 'ar' ? 'هذا العميل مغلق (مفقود).' : 'This lead is closed (lost).')}
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => onReopen(lead.id)}
              style={{ fontWeight: 800, whiteSpace: 'nowrap', borderColor: 'var(--accent)', color: 'var(--accent)' }}
            >
              {lang === 'ar' ? '↻ إعادة فتح العميل' : '↻ Reopen lead'}
            </button>
          </div>
        )}

        {/* ── Status Update: 3 Outcome Cards ── */}
        {showOutcomeCards && (
          <div style={{ marginTop: 16 }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: 12 }}>{t('wizUpdateStatus')}</h3>

            {/* Outcome cards — the visit flow when a visit is scheduled, else the contacted flow */}
            <div className="wizard-outcomes">
              {isVisitFlow ? (
                <>
                  <button
                    className={`wizard-outcome-card ${selectedOutcome === 'visited' ? 'selected' : ''}`}
                    style={{ '--card-color': '#22c55e' }}
                    onClick={() => { setSelectedOutcome(selectedOutcome === 'visited' ? null : 'visited'); setOutcomeErrors({}); }}
                  >
                    <span className="wizard-outcome-icon">&#9989;</span>
                    <span className="wizard-outcome-label">{t('wizVisitHappened')}</span>
                    <span className="wizard-outcome-desc">{t('wizVisitHappenedDesc')}</span>
                  </button>
                  <button
                    className={`wizard-outcome-card ${selectedOutcome === 'no_show' ? 'selected' : ''}`}
                    style={{ '--card-color': '#f59e0b' }}
                    onClick={() => { setSelectedOutcome(selectedOutcome === 'no_show' ? null : 'no_show'); setOutcomeErrors({}); }}
                  >
                    <span className="wizard-outcome-icon">&#10060;</span>
                    <span className="wizard-outcome-label">{t('wizVisitMissed')}</span>
                    <span className="wizard-outcome-desc">{t('wizVisitMissedDesc')}</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    className={`wizard-outcome-card ${selectedOutcome === 'interested' ? 'selected' : ''}`}
                    style={{ '--card-color': '#22c55e' }}
                    onClick={() => { setSelectedOutcome(selectedOutcome === 'interested' ? null : 'interested'); setOutcomeErrors({}); }}
                  >
                    <span className="wizard-outcome-icon">&#9989;</span>
                    <span className="wizard-outcome-label">{t('wizInterested')}</span>
                    <span className="wizard-outcome-desc">{t('wizInterestedDesc')}</span>
                  </button>
                  <button
                    className={`wizard-outcome-card ${selectedOutcome === 'not_answering' ? 'selected' : ''}`}
                    style={{ '--card-color': '#f59e0b' }}
                    onClick={() => { setSelectedOutcome(selectedOutcome === 'not_answering' ? null : 'not_answering'); setOutcomeErrors({}); }}
                  >
                    <span className="wizard-outcome-icon">&#128222;</span>
                    <span className="wizard-outcome-label">{t('wizNotAnswering')}</span>
                    <span className="wizard-outcome-desc">{t('wizNotAnsweringDesc')}</span>
                  </button>
                  <button
                    className={`wizard-outcome-card ${selectedOutcome === 'not_interested' ? 'selected' : ''}`}
                    style={{ '--card-color': '#ef4444' }}
                    onClick={() => { setSelectedOutcome(selectedOutcome === 'not_interested' ? null : 'not_interested'); setOutcomeErrors({}); }}
                  >
                    <span className="wizard-outcome-icon">&#10060;</span>
                    <span className="wizard-outcome-label">{t('wizNotInterested')}</span>
                    <span className="wizard-outcome-desc">{t('wizNotInterestedDesc')}</span>
                  </button>
                </>
              )}
            </div>

            {/* ── Contextual fields based on selected outcome ── */}
            {selectedOutcome === 'interested' && (
              <div className="wizard-form" style={{ marginTop: 16, padding: 16, background: 'rgba(34,197,94,0.05)', borderRadius: 12, border: '1px solid rgba(34,197,94,0.15)' }}>
                <div className="input-group">
                  <label>{t('wizStatusNote')} *</label>
                  <textarea rows={2} value={outcomeData.statusNote} onChange={e => ud('statusNote', e.target.value)} placeholder={t('wizStatusNotePlaceholder')}
                    style={{ borderColor: outcomeErrors.statusNote ? 'var(--error)' : undefined }} />
                  {outcomeErrors.statusNote && <span className="wizard-error">{t('wizRequired')}</span>}
                </div>
                <div className="input-group">
                  <label>{t('wizSelectProject')} *</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, ...(outcomeErrors.project ? { outline: '1px solid var(--error)', borderRadius: 8, padding: 4 } : {}) }}>
                    {PROJECTS.map(p => {
                      const sel = (outcomeData.project || '').split(',').map(s => s.trim()).includes(p);
                      return (
                        <button type="button" key={p} onClick={() => {
                          const set = new Set((outcomeData.project || '').split(',').map(s => s.trim()).filter(Boolean));
                          if (set.has(p)) set.delete(p); else set.add(p);
                          ud('project', [...set].join(','));
                        }} style={{ padding: '5px 10px', borderRadius: 8, border: `1px solid ${sel ? 'var(--accent)' : 'var(--border)'}`, background: sel ? 'var(--accent)' : 'transparent', color: sel ? '#fff' : 'var(--text-primary)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                          {sel ? '✓ ' : ''}{projLabel(p, t)}
                        </button>
                      );
                    })}
                  </div>
                  {outcomeErrors.project && <span className="wizard-error">{t('wizRequired')}</span>}
                </div>
                <div className="input-group">
                  <label>{t('wizPaymentMethod')}</label>
                  <div className="wizard-toggle-group">
                    {PAYMENT_METHODS.map(pm => (
                      <button key={pm} type="button"
                        className={`wizard-toggle-btn ${outcomeData.paymentMethod === pm ? 'active' : ''}`}
                        onClick={() => ud('paymentMethod', outcomeData.paymentMethod === pm ? '' : pm)}
                      >{t('wizPayment_' + pm)}</button>
                    ))}
                  </div>
                </div>
                <div className="input-group">
                  <label>{t('wizArrangeVisit')} *</label>
                  <div className="wizard-toggle-group" style={{ borderColor: outcomeErrors.siteVisit ? 'var(--error)' : undefined, border: outcomeErrors.siteVisit ? '1px solid var(--error)' : undefined, borderRadius: outcomeErrors.siteVisit ? 8 : undefined, padding: outcomeErrors.siteVisit ? 4 : undefined }}>
                    <button type="button" className={`wizard-toggle-btn ${outcomeData.siteVisit === true ? 'active' : ''}`}
                      style={outcomeData.siteVisit === true ? { background: '#22c55e22', borderColor: '#22c55e', color: '#22c55e' } : {}}
                      onClick={() => ud('siteVisit', true)}>{t('wizYes')}</button>
                    <button type="button" className={`wizard-toggle-btn ${outcomeData.siteVisit === false ? 'active' : ''}`}
                      onClick={() => ud('siteVisit', false)}>{t('wizNo')}</button>
                  </div>
                  {outcomeErrors.siteVisit && <span className="wizard-error">{t('wizRequired')}</span>}
                </div>
                {outcomeData.siteVisit === true && (
                  <div className="input-group">
                    <label>{t('wizVisitDateTime')} *</label>
                    <input type="datetime-local" value={outcomeData.siteVisitDate} onChange={e => ud('siteVisitDate', e.target.value)}
                      style={{ borderColor: outcomeErrors.siteVisitDate ? 'var(--error)' : undefined }} />
                    {outcomeErrors.siteVisitDate && <span className="wizard-error">{t('wizRequired')}</span>}
                  </div>
                )}
                {outcomeData.siteVisit === false && (
                  <>
                    <div className="input-group">
                      <label>{t('wizNoVisitReason')} *</label>
                      <textarea rows={2} value={outcomeData.noVisitReason} onChange={e => ud('noVisitReason', e.target.value)}
                        placeholder={t('wizNoVisitReasonPlaceholder')}
                        style={{ borderColor: outcomeErrors.noVisitReason ? 'var(--error)' : undefined }} />
                      {outcomeErrors.noVisitReason && <span className="wizard-error">{t('wizRequired')}</span>}
                    </div>
                    <div className="input-group">
                      <label>{t('wizFollowUpDate')} *</label>
                      <input type="datetime-local" value={outcomeData.followUpDate} onChange={e => ud('followUpDate', e.target.value)}
                        style={{ borderColor: outcomeErrors.followUpDate ? 'var(--error)' : undefined }} />
                      {outcomeErrors.followUpDate && <span className="wizard-error">{t('wizRequired')}</span>}
                    </div>
                  </>
                )}
                <button className="btn btn-sm" onClick={handleOutcomeSubmit} disabled={saving}
                  style={{ background: '#22c55e', color: '#fff', fontWeight: 700, marginTop: 4, alignSelf: 'flex-start' }}>
                  {saving ? '...' : t('wizConfirm')}
                </button>
              </div>
            )}

            {selectedOutcome === 'not_answering' && (
              <div className="wizard-form" style={{ marginTop: 16, padding: 16, background: 'rgba(245,158,11,0.05)', borderRadius: 12, border: '1px solid rgba(245,158,11,0.15)' }}>
                <div className="input-group">
                  <label>{t('wizStatusNote')} *</label>
                  <textarea rows={2} value={outcomeData.statusNote} onChange={e => ud('statusNote', e.target.value)} placeholder={t('wizStatusNotePlaceholder')}
                    style={{ borderColor: outcomeErrors.statusNote ? 'var(--error)' : undefined }} />
                  {outcomeErrors.statusNote && <span className="wizard-error">{t('wizRequired')}</span>}
                </div>
                <div className="input-group">
                  <label>{t('wizFollowUpDate')} *</label>
                  <input type="datetime-local" value={outcomeData.followUpDate} onChange={e => ud('followUpDate', e.target.value)}
                    style={{ borderColor: outcomeErrors.followUpDate ? 'var(--error)' : undefined }} />
                  {outcomeErrors.followUpDate && <span className="wizard-error">{t('wizRequired')}</span>}
                </div>
                <button className="btn btn-sm" onClick={handleOutcomeSubmit} disabled={saving}
                  style={{ background: '#f59e0b', color: '#fff', fontWeight: 700, marginTop: 4, alignSelf: 'flex-start' }}>
                  {saving ? '...' : t('wizConfirm')}
                </button>
              </div>
            )}

            {selectedOutcome === 'not_interested' && (
              <div className="wizard-form" style={{ marginTop: 16, padding: 16, background: 'rgba(239,68,68,0.05)', borderRadius: 12, border: '1px solid rgba(239,68,68,0.15)' }}>
                {!skipStatusNote && (
                <div className="input-group">
                  <label>{t('wizStatusNote')} *</label>
                  <textarea rows={2} value={outcomeData.statusNote} onChange={e => ud('statusNote', e.target.value)} placeholder={t('wizStatusNotePlaceholder')}
                    style={{ borderColor: outcomeErrors.statusNote ? 'var(--error)' : undefined }} />
                  {outcomeErrors.statusNote && <span className="wizard-error">{t('wizRequired')}</span>}
                </div>
                )}
                <div className="input-group">
                  <label>{t('crmLossReason')} *</label>
                  <select value={outcomeData.lossReason} onChange={e => ud('lossReason', e.target.value)}
                    style={{ borderColor: outcomeErrors.lossReason ? 'var(--error)' : undefined }}>
                    <option value="">{t('crmSelectReason')}</option>
                    {LOSS_REASONS.map(k => <option key={k} value={t(k)}>{t(k)}</option>)}
                  </select>
                  {outcomeErrors.lossReason && <span className="wizard-error">{t('wizRequired')}</span>}
                </div>
                <div className="input-group">
                  <label>{t('wizLossDetails')} *</label>
                  <textarea rows={2} value={outcomeData.lossDetails} onChange={e => ud('lossDetails', e.target.value)}
                    placeholder={t('crmLossDetailsPlaceholder')}
                    style={{ borderColor: outcomeErrors.lossDetails ? 'var(--error)' : undefined }} />
                  {outcomeErrors.lossDetails && <span className="wizard-error">{t('wizRequired')}</span>}
                </div>
                <button className="btn btn-sm" onClick={handleOutcomeSubmit} disabled={saving}
                  style={{ background: '#ef4444', color: '#fff', fontWeight: 700, marginTop: 4, alignSelf: 'flex-start' }}>
                  {saving ? '...' : t('wizConfirm')}
                </button>
              </div>
            )}

            {/* Site-visit flow → the customer visited */}
            {selectedOutcome === 'visited' && (
              <div className="wizard-form" style={{ marginTop: 16, padding: 16, background: 'rgba(34,197,94,0.05)', borderRadius: 12, border: '1px solid rgba(34,197,94,0.15)' }}>
                <div className="input-group">
                  <label>{t('wizStatusNote')} *</label>
                  <textarea rows={2} value={outcomeData.statusNote} onChange={e => ud('statusNote', e.target.value)} placeholder={t('wizStatusNotePlaceholder')}
                    style={{ borderColor: outcomeErrors.statusNote ? 'var(--error)' : undefined }} />
                  {outcomeErrors.statusNote && <span className="wizard-error">{t('wizRequired')}</span>}
                </div>
                <div className="input-group">
                  <label>{t('wizCustomerOpinion')} *</label>
                  <div className="wizard-toggle-group" style={outcomeErrors.visitOpinion ? { border: '1px solid var(--error)', borderRadius: 8, padding: 4 } : {}}>
                    <button type="button" className={`wizard-toggle-btn ${outcomeData.visitOpinion === 'interested' ? 'active' : ''}`}
                      style={outcomeData.visitOpinion === 'interested' ? { background: '#22c55e22', borderColor: '#22c55e', color: '#22c55e' } : {}}
                      onClick={() => ud('visitOpinion', 'interested')}>{t('wizInterested')}</button>
                    <button type="button" className={`wizard-toggle-btn ${outcomeData.visitOpinion === 'not_interested' ? 'active' : ''}`}
                      onClick={() => ud('visitOpinion', 'not_interested')}>{t('wizNotInterested')}</button>
                  </div>
                  {outcomeErrors.visitOpinion && <span className="wizard-error">{t('wizRequired')}</span>}
                </div>
                {outcomeData.visitOpinion === 'interested' && (
                  <div className="input-group">
                    <label>{t('wizFollowUpDate')} *</label>
                    <input type="datetime-local" value={outcomeData.followUpDate} onChange={e => ud('followUpDate', e.target.value)}
                      style={{ borderColor: outcomeErrors.followUpDate ? 'var(--error)' : undefined }} />
                    {outcomeErrors.followUpDate && <span className="wizard-error">{t('wizRequired')}</span>}
                  </div>
                )}
                {outcomeData.visitOpinion === 'not_interested' && (
                  <>
                    <div className="input-group">
                      <label>{t('crmLossReason')} *</label>
                      <select value={outcomeData.lossReason} onChange={e => ud('lossReason', e.target.value)}
                        style={{ borderColor: outcomeErrors.lossReason ? 'var(--error)' : undefined }}>
                        <option value="">{t('crmSelectReason')}</option>
                        {LOSS_REASONS.map(k => <option key={k} value={t(k)}>{t(k)}</option>)}
                      </select>
                      {outcomeErrors.lossReason && <span className="wizard-error">{t('wizRequired')}</span>}
                    </div>
                    <div className="input-group">
                      <label>{t('wizLossDetails')} *</label>
                      <textarea rows={2} value={outcomeData.lossDetails} onChange={e => ud('lossDetails', e.target.value)}
                        placeholder={t('crmLossDetailsPlaceholder')}
                        style={{ borderColor: outcomeErrors.lossDetails ? 'var(--error)' : undefined }} />
                      {outcomeErrors.lossDetails && <span className="wizard-error">{t('wizRequired')}</span>}
                    </div>
                  </>
                )}
                <button className="btn btn-sm" onClick={handleOutcomeSubmit} disabled={saving}
                  style={{ background: '#22c55e', color: '#fff', fontWeight: 700, marginTop: 4, alignSelf: 'flex-start' }}>
                  {saving ? '...' : t('wizConfirm')}
                </button>
              </div>
            )}

            {/* Site-visit flow → the customer did NOT visit */}
            {selectedOutcome === 'no_show' && (
              <div className="wizard-form" style={{ marginTop: 16, padding: 16, background: 'rgba(245,158,11,0.05)', borderRadius: 12, border: '1px solid rgba(245,158,11,0.15)' }}>
                <div className="input-group">
                  <label>{t('wizStatusNote')} *</label>
                  <textarea rows={2} value={outcomeData.statusNote} onChange={e => ud('statusNote', e.target.value)} placeholder={t('wizStatusNotePlaceholder')}
                    style={{ borderColor: outcomeErrors.statusNote ? 'var(--error)' : undefined }} />
                  {outcomeErrors.statusNote && <span className="wizard-error">{t('wizRequired')}</span>}
                </div>
                <div className="input-group">
                  <label>{t('wizNoVisitReason')} *</label>
                  <textarea rows={2} value={outcomeData.noVisitReason} onChange={e => ud('noVisitReason', e.target.value)}
                    placeholder={t('wizNoVisitReasonPlaceholder')}
                    style={{ borderColor: outcomeErrors.noVisitReason ? 'var(--error)' : undefined }} />
                  {outcomeErrors.noVisitReason && <span className="wizard-error">{t('wizRequired')}</span>}
                </div>
                <div className="input-group">
                  <label>{t('wizNextAction')} *</label>
                  <div className="wizard-toggle-group" style={outcomeErrors.visitNextAction ? { border: '1px solid var(--error)', borderRadius: 8, padding: 4 } : {}}>
                    <button type="button" className={`wizard-toggle-btn ${outcomeData.visitNextAction === 'reschedule' ? 'active' : ''}`}
                      onClick={() => ud('visitNextAction', 'reschedule')}>{t('wizRescheduleVisit')}</button>
                    <button type="button" className={`wizard-toggle-btn ${outcomeData.visitNextAction === 'followup' ? 'active' : ''}`}
                      onClick={() => ud('visitNextAction', 'followup')}>{t('wizFollowUp')}</button>
                  </div>
                  {outcomeErrors.visitNextAction && <span className="wizard-error">{t('wizRequired')}</span>}
                </div>
                {outcomeData.visitNextAction === 'reschedule' && (
                  <div className="input-group">
                    <label>{t('wizVisitDateTime')} *</label>
                    <input type="datetime-local" value={outcomeData.siteVisitDate} onChange={e => ud('siteVisitDate', e.target.value)}
                      style={{ borderColor: outcomeErrors.siteVisitDate ? 'var(--error)' : undefined }} />
                    {outcomeErrors.siteVisitDate && <span className="wizard-error">{t('wizRequired')}</span>}
                  </div>
                )}
                {outcomeData.visitNextAction === 'followup' && (
                  <div className="input-group">
                    <label>{t('wizFollowUpDate')} *</label>
                    <input type="datetime-local" value={outcomeData.followUpDate} onChange={e => ud('followUpDate', e.target.value)}
                      style={{ borderColor: outcomeErrors.followUpDate ? 'var(--error)' : undefined }} />
                    {outcomeErrors.followUpDate && <span className="wizard-error">{t('wizRequired')}</span>}
                  </div>
                )}
                <button className="btn btn-sm" onClick={handleOutcomeSubmit} disabled={saving}
                  style={{ background: '#f59e0b', color: '#fff', fontWeight: 700, marginTop: 4, alignSelf: 'flex-start' }}>
                  {saving ? '...' : t('wizConfirm')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Priority Directive (admin/manager → rep) ── */}
        {canDirective && (
          <div style={{ marginTop: 20, padding: 14, borderRadius: 12, border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: '1rem' }}>🚩</span>
              <strong style={{ fontSize: '0.92rem', color: '#ef4444' }}>{lang === 'ar' ? 'إرسال توجيه عاجل' : 'Send Priority Directive'}</strong>
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.5 }}>
              {lang === 'ar'
                ? 'يُعلَّم العميل كأولوية ويظهر في مهام اليوم لدى موظف المبيعات فوراً مع إشعار.'
                : 'Flags the lead as priority and surfaces it in the rep’s tasks today with a push notification.'}
            </div>
            <textarea
              value={directiveText}
              onChange={e => setDirectiveText(e.target.value)}
              placeholder={lang === 'ar' ? 'اكتب التوجيه لموظف المبيعات…' : 'Write a directive for the rep…'}
              rows={2}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.85rem', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                value={directiveAssignee}
                onChange={e => setDirectiveAssignee(e.target.value)}
                style={{ flex: '1 1 180px', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.83rem' }}
              >
                <option value="">{lang === 'ar' ? `إبقاء موظف المبيعات الحالي${form.assignedTo ? ' (' + form.assignedTo + ')' : ''}` : `Keep current rep${form.assignedTo ? ' (' + form.assignedTo + ')' : ''}`}</option>
                {salesTeam.filter(m => (m.name || m) !== form.assignedTo).map((m, idx) => {
                  const nm = m.name || m;
                  return <option key={idx} value={nm}>{lang === 'ar' ? 'إعادة الإسناد إلى: ' : 'Reassign to: '}{nm}</option>;
                })}
              </select>
              <button
                onClick={sendDirectiveNow}
                disabled={!directiveText.trim() || busyDir}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: directiveText.trim() && !busyDir ? '#ef4444' : 'var(--border)', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: directiveText.trim() && !busyDir ? 'pointer' : 'not-allowed' }}
              >
                {busyDir ? '…' : (lang === 'ar' ? 'إرسال التوجيه' : 'Send Directive')}
              </button>
            </div>
          </div>
        )}

        {/* ── Reply to directive (assigned rep) ── */}
        {hasDirective && !canDirective && (
          <div style={{ marginTop: 20, padding: 14, borderRadius: 12, border: '1px solid rgba(34,197,94,0.35)', background: 'rgba(34,197,94,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: '1rem' }}>↩</span>
              <strong style={{ fontSize: '0.92rem', color: '#16a34a' }}>{lang === 'ar' ? 'الرد على التوجيه' : 'Reply to Directive'}</strong>
            </div>
            <textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder={lang === 'ar' ? 'اكتب ردك للمدير…' : 'Write your reply to the manager…'}
              rows={2}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.85rem', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                onClick={sendReplyNow}
                disabled={!replyText.trim() || busyDir}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: replyText.trim() && !busyDir ? '#16a34a' : 'var(--border)', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: replyText.trim() && !busyDir ? 'pointer' : 'not-allowed' }}
              >
                {busyDir ? '…' : (lang === 'ar' ? 'إرسال الرد' : 'Send Reply')}
              </button>
            </div>
          </div>
        )}

        {/* ── Communication Log (genuine comms only; status-change notes live in the
              Status History section below) ── */}
        {visibleComms.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              {t('crmCommunicationLog')}
              <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-secondary)' }}>({visibleComms.length})</span>
            </h3>
            <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {visibleComms.map((c, i) => {
                const isDir = c.type === 'directive';
                const isReply = c.type === 'directive_reply';
                const label = isDir ? (lang === 'ar' ? 'توجيه عاجل' : 'Priority Directive')
                  : isReply ? (lang === 'ar' ? 'رد على التوجيه' : 'Directive Reply')
                  : (t('crmCommType_' + c.type) || c.type);
                return (
                <div key={c.id || i} style={{
                  background: isDir ? 'rgba(239,68,68,0.07)' : isReply ? 'rgba(34,197,94,0.07)' : 'var(--bg-secondary)',
                  borderRadius: 10, padding: '10px 14px',
                  border: '1px solid ' + (isDir ? 'rgba(239,68,68,0.35)' : isReply ? 'rgba(34,197,94,0.35)' : 'var(--border)'),
                  borderInlineStart: (isDir || isReply) ? '3px solid ' + (isDir ? '#ef4444' : '#22c55e') : undefined,
                  fontSize: '0.83rem', marginInlineStart: isReply ? 18 : 0,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <strong style={{ color: isDir ? '#ef4444' : isReply ? '#16a34a' : undefined }}>
                      {(isDir ? '🚩 ' : isReply ? '↩ ' : '') + label}
                    </strong>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {c.date ? new Date(c.date).toLocaleString() : ''}
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-primary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{(c.message || '').replace(/\n?<!--meta:[\s\S]*?-->/g, '').trim()}</div>
                  {c.by && (() => {
                    const other = c.by !== (lead.assignedTo || '');
                    return <div style={{ marginTop: 4, fontSize: '0.78rem', fontWeight: other ? 700 : 400, color: other ? '#b45309' : 'var(--text-secondary)' }}>
                      {other ? (lang === 'ar' ? '🚩 بواسطة (ليس المسؤول عن العميل): ' : '🚩 by (not the lead owner): ') : `${t('crmBy')}: `}
                      <strong style={{ color: other ? '#b45309' : 'var(--text-primary)' }}>{c.by}</strong>
                    </div>;
                  })()}
                </div>
              );})}
            </div>
          </div>
        )}

        {/* ── Status History Log ── */}
        <div style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            {t('crmStatusHistory')}
            <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-secondary)' }}>({transitions.length})</span>
          </h3>
          {loadingHistory ? (
            <div style={{ textAlign: 'center', padding: 16, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{t('loading')}</div>
          ) : transitions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 16, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{t('crmNoHistory')}</div>
          ) : (
            <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {transitions.map((tr, i) => {
                let data = {};
                try { data = typeof tr.data === 'string' ? JSON.parse(tr.data) : (tr.data || {}); } catch {}
                const outcomeColor = tr.outcome === 'not_interested' ? '#ef4444' : tr.outcome === 'interested' ? '#22c55e' : tr.outcome === 'not_answering' ? '#f59e0b' : 'var(--text-secondary)';
                return (
                  <div key={tr.id || i} style={{
                    background: 'var(--bg-secondary)', borderRadius: 10, padding: '10px 14px',
                    border: '1px solid var(--border)', fontSize: '0.83rem',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                          background: outcomeColor, flexShrink: 0,
                        }} />
                        <strong>{t('crmStatus_' + tr.from_status)}</strong>
                        <span style={{ color: 'var(--text-secondary)' }}>&rarr;</span>
                        <strong>{t('crmStatus_' + tr.to_status)}</strong>
                      </div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {new Date(tr.created_at).toLocaleString()}
                      </span>
                    </div>
                    {data.statusNote && (
                      <div style={{ margin: '4px 0 6px', padding: '6px 10px', background: 'rgba(99,102,241,0.06)', borderRadius: 8, borderInlineStart: '3px solid var(--accent)', fontSize: '0.82rem', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                        {data.statusNote}
                      </div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                      {tr.done_by && (tr.done_by !== (lead.assignedTo || '')
                        ? <span style={{ fontWeight: 700, color: '#b45309' }}>🚩 {lang === 'ar' ? 'بواسطة (ليس المسؤول): ' : 'by (not the owner): '}{tr.done_by}</span>
                        : <span>{t('crmBy')}: <strong style={{ color: 'var(--text-primary)' }}>{tr.done_by}</strong></span>)}
                      {tr.outcome && <span>{t('crmOutcome')}: <strong style={{ color: outcomeColor }}>{t('crmOutcome_' + tr.outcome) || tr.outcome}</strong></span>}
                      {data.lossReason && <span>{t('crmLossReason')}: <strong style={{ color: 'var(--text-primary)' }}>{data.lossReason}</strong></span>}
                      {data.lossDetails && <span>{t('wizLossDetails')}: <strong style={{ color: 'var(--text-primary)' }}>{data.lossDetails}</strong></span>}
                      {data.project && <span>{t('crmProject')}: <strong style={{ color: 'var(--text-primary)' }}>{data.project}</strong></span>}
                      {data.followUpDate && <span>{t('wizFollowUpDate')}: <strong style={{ color: 'var(--text-primary)' }}>{new Date(data.followUpDate).toLocaleString()}</strong></span>}
                      {data.siteVisitDate && <span>{t('wizVisitDateTime')}: <strong style={{ color: 'var(--text-primary)' }}>{new Date(data.siteVisitDate).toLocaleString()}</strong></span>}
                      {data.offeredPrice && <span>{t('crmOfferedPrice')}: <strong style={{ color: 'var(--text-primary)' }}>{data.offeredPrice} SAR</strong></span>}
                      {data.finalPrice && <span>{t('crmFinalPrice')}: <strong style={{ color: 'var(--text-primary)' }}>{data.finalPrice} SAR</strong></span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="crm-modal-footer">
          {confirmDelete ? (
            <>
              <span style={{ fontSize: '0.85rem', color: 'var(--error)', fontWeight: 600 }}>{t('crmConfirmDelete')}</span>
              <button className="btn btn-sm" style={{ background: 'var(--error)', color: '#fff' }} onClick={() => onDelete(lead.id)}>
                {t('crmYesDelete')}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setConfirmDelete(false)}>{t('crmCancel')}</button>
            </>
          ) : (
            <>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? '...' : t('crmSaveLead')}</button>
              {can('leads.delete') && (
                <button className="btn btn-sm" style={{ background: 'var(--error)', color: '#fff' }} onClick={() => setConfirmDelete(true)}>
                  {t('crmDeleteLead')}
                </button>
              )}
              <button className="btn btn-secondary" onClick={onClose}>{t('crmCancel')}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Placeholder - will be replaced by fetched team
let SALES_TEAM_PLACEHOLDER = ['Ahmed','Sara','Mohammed','Fatima','Omar'];

// Admin bulk import: upload a CSV (columns name,phone,source,project,campaign,notes);
// the server dedups every phone against the whole CRM and auto-distributes the rest.
function BulkImportButton({ lang, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const ar = lang === 'ar';

  function parseCSV(text) {
    text = text.replace(/^﻿/, '');
    const rows = []; let row = []; let field = ''; let inQ = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQ) {
        if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else { inQ = false; } }
        else field += c;
      } else if (c === '"') { inQ = true; }
      else if (c === ',') { row.push(field); field = ''; }
      else if (c === '\r') { /* skip */ }
      else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
      else field += c;
    }
    if (field.length || row.length) { row.push(field); rows.push(row); }
    return rows.filter(r => r.some(c => String(c).trim() !== ''));
  }

  async function onFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const rows = parseCSV(await file.text());
      if (rows.length < 2) { setResult({ error: ar ? 'الملف فارغ' : 'Empty file' }); setBusy(false); return; }
      const headers = rows[0].map(h => String(h).trim().toLowerCase());
      const col = (names) => { for (const n of names) { const i = headers.indexOf(n); if (i >= 0) return i; } return -1; };
      const ci = { name: col(['name', 'الاسم']), phone: col(['phone', 'رقم الهاتف', 'الهاتف']), source: col(['source', 'المصدر']), project: col(['project', 'المشروع']), campaign: col(['campaign', 'الحملة']), notes: col(['notes', 'ملاحظات']) };
      if (ci.name < 0 || ci.phone < 0) { setResult({ error: ar ? 'الملف يحتاج عمودَي name و phone' : 'File needs name and phone columns' }); setBusy(false); return; }
      const leads = rows.slice(1).map(r => ({
        name: r[ci.name], phone: r[ci.phone],
        source: ci.source >= 0 ? r[ci.source] : '',
        project: ci.project >= 0 ? r[ci.project] : '',
        campaign: ci.campaign >= 0 ? r[ci.campaign] : '',
        notes: ci.notes >= 0 ? r[ci.notes] : '',
      })).filter(l => String(l.name || '').trim() && String(l.phone || '').trim());
      const res = await bulkImportLeads(leads);
      setResult(res);
      if (!res.error && res.imported > 0 && onDone) onDone();
    } catch (err) { setResult({ error: err.message }); }
    setBusy(false);
  }

  return (
    <>
      <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={onFile} />
      <button className="btn btn-secondary" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}>
        {busy ? '…' : (ar ? '⬆ استيراد جماعي' : '⬆ Bulk import')}
      </button>
      {result && (
        <div onClick={() => setResult(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: 14, padding: 24, minWidth: 300, maxWidth: 440 }}>
            {result.error ? (
              <div style={{ color: 'var(--error)', fontWeight: 600 }}>{(ar ? 'فشل الاستيراد: ' : 'Import failed: ') + result.error}</div>
            ) : (
              <>
                <h3 style={{ margin: '0 0 12px', fontWeight: 800 }}>{ar ? 'اكتمل الاستيراد' : 'Import complete'}</h3>
                <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                  <div><div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--success)' }}>{result.imported}</div><div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{ar ? 'أُضيفوا وُوزّعوا' : 'Added & distributed'}</div></div>
                  <div><div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--accent)' }}>{result.duplicate}</div><div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{ar ? 'مكرر (تخطّي)' : 'Duplicate skipped'}</div></div>
                  {result.skipped > 0 && <div><div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-muted)' }}>{result.skipped}</div><div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{ar ? 'غير صالح' : 'Invalid'}</div></div>}
                </div>
                {result.distribution && Object.keys(result.distribution).length > 0 && (
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, marginBottom: 6 }}>{ar ? 'التوزيع على الفريق' : 'Distribution'}</div>
                    {Object.entries(result.distribution).map(([who, n]) => (
                      <div key={who} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 2 }}><span>{who}</span><span style={{ fontWeight: 700 }}>{n}</span></div>
                    ))}
                  </div>
                )}
              </>
            )}
            <button className="btn btn-primary" style={{ marginTop: 16, width: '100%' }} onClick={() => setResult(null)}>{ar ? 'إغلاق' : 'Close'}</button>
          </div>
        </div>
      )}
    </>
  );
}

function AddLeadModal({ onClose, onSave, role, salesTeam, brokerCompanies = [], userName, t, lang }) {
  const isSales = role !== 'admin';
  const [form, setForm] = useState({ name: '', phone: '', email: '', source: isSales ? 'site_visitor' : 'snapchat', campaign: '', project: '', budget: 0, notes: '', assignedTo: isSales ? userName || '' : '', brokerCompany: '' });
  useEffect(() => {
    const handleEsc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const handleSave = async () => {
    if (!form.name.trim() || !form.phone.trim() || !form.project) return;
    setSaving(true);
    await onSave({
      ...form, id: genId(), status: 'new',
      adminApproval: 'approved',
      createdBy: userName || '',
    });
    setSaving(false);
  };
  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="crm-modal-header">
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>{t('crmAddLead')}</h2>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>&times;</button>
        </div>
        <div className="crm-form-grid">
          <div className="input-group"><label>{t('crmLeadName')} *</label><input value={form.name} onChange={e => u('name', e.target.value)} autoFocus /></div>
          <div className="input-group"><label>{t('crmPhone')} *</label><input value={form.phone} onChange={e => u('phone', e.target.value)} style={{ direction: 'ltr' }} /></div>
          <div className="input-group"><label>{t('crmEmail')}</label><input value={form.email} onChange={e => u('email', e.target.value)} style={{ direction: 'ltr' }} /></div>
          <div className="input-group"><label>{t('crmSource')}</label>
            {isSales ? (
              <select value={form.source} onChange={e => u('source', e.target.value)}>
                {['site_visitor', 'aqar', 'whatsapp'].map(s => <option key={s} value={s}>{sourceLabel(s, lang)}</option>)}
              </select>
            ) : (
              <select value={form.source} onChange={e => u('source', e.target.value)}>{SOURCES.map(s => <option key={s} value={s}>{sourceLabel(s, lang)}</option>)}</select>
            )}
          </div>
          <div className="input-group"><label>{t('crmCampaign')}</label><input value={form.campaign} onChange={e => u('campaign', e.target.value)} /></div>
          <div className="input-group"><label>{t('crmProject')} *</label><select value={form.project} onChange={e => u('project', e.target.value)} required><option value="">{t('wizChooseProject')}</option>{PROJECTS.map(p => <option key={p} value={p}>{projLabel(p, t)}</option>)}</select></div>
          <div className="input-group"><label>{t('crmBudget')}</label><input type="number" value={form.budget || ''} onChange={e => u('budget', Number(e.target.value) || 0)} /></div>
          {role === 'admin' && (
            <div className="input-group"><label>{t('crmAssignedTo')}</label>
              <select
                value={form.brokerCompany ? 'broker:' + form.brokerCompany : (form.assignedTo ? 'emp:' + form.assignedTo : '')}
                onChange={e => {
                  const v = e.target.value;
                  if (v.startsWith('broker:')) setForm(p => ({ ...p, brokerCompany: v.slice(7), assignedTo: '', source: 'broker' }));
                  else if (v.startsWith('emp:')) setForm(p => ({ ...p, assignedTo: v.slice(4), brokerCompany: '' }));
                  else setForm(p => ({ ...p, assignedTo: '', brokerCompany: '' }));
                }}>
                <option value="">{lang === 'ar' ? '\u2014 (\u062a\u0648\u0632\u064a\u0639 \u062a\u0644\u0642\u0627\u0626\u064a)' : '\u2014 (auto-distribute)'}</option>
                <optgroup label={lang === 'ar' ? '\u0627\u0644\u0645\u0648\u0638\u0641\u0648\u0646' : 'Employees'}>
                  {salesTeam.map(s => <option key={s} value={'emp:' + s}>{s}</option>)}
                </optgroup>
                {brokerCompanies.length > 0 && (
                  <optgroup label={lang === 'ar' ? '\u0627\u0644\u0648\u0633\u0637\u0627\u0621' : 'Brokers'}>
                    {brokerCompanies.map(b => <option key={b} value={'broker:' + b}>{b}</option>)}
                  </optgroup>
                )}
              </select>
              {form.brokerCompany && <div style={{ fontSize: '0.68rem', color: '#7C3AED', marginTop: 3 }}>{lang === 'ar' ? '\u0633\u064a\u064f\u062d\u0648\u064e\u0651\u0644 \u0627\u0644\u0639\u0645\u064a\u0644 \u0625\u0644\u0649 \u0627\u0644\u0648\u0633\u064a\u0637 \u0644\u064a\u0648\u0632\u0651\u0639\u0647 \u0639\u0644\u0649 \u0641\u0631\u064a\u0642\u0647' : 'Lead goes to the broker to distribute to their team'}</div>}
            </div>
          )}
          <div className="input-group" style={{ gridColumn: '1 / -1' }}><label>{t('crmNotes')}</label><textarea rows={2} value={form.notes} onChange={e => u('notes', e.target.value)} /></div>
        </div>
        <div className="crm-modal-footer">
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? '...' : t('crmSaveLead')}</button>
          <button className="btn btn-secondary" onClick={onClose}>{t('crmCancel')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Analytics View ──
const tooltipStyle = { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text-primary)', boxShadow: 'var(--card-hover-shadow)' };

function AnalyticsView({ t, lang }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/api/leads/analytics');
        setData(res.data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-secondary)' }}>{t('loading')}</div>;
  if (!data) return null;

  const { salesPerformance, pipeline, bySource, overall } = data;
  const fmtH = h => h == null ? '—' : h < 1 ? (h * 60).toFixed(0) + ' min' : h < 24 ? h.toFixed(1) + ' hrs' : (h / 24).toFixed(1) + ' days';
  const SCOLORS = { new: '#6366f1', contacted: '#3b82f6', interested: '#8b5cf6', site_visit_scheduled: '#f59e0b', site_visit_done: '#f97316', offer_sent: '#06b6d4', negotiating: '#ec4899', won: '#22c55e', lost: '#ef4444' };

  return (
    <div>
      {/* Overall KPIs */}
      <div className="crm-stats-bar mb-6">
        <div className="crm-stat-item">
          <span className="crm-stat-value">{overall.total}</span>
          <span className="crm-stat-label">{t('crmTotalLeads')}</span>
        </div>
        <div className="crm-stat-item">
          <span className="crm-stat-value" style={{ color: '#8b5cf6' }}>{overall.potential}</span>
          <span className="crm-stat-label">{t('crmPotential') || 'Potential'}</span>
        </div>
        <div className="crm-stat-item">
          <span className="crm-stat-value" style={{ color: 'var(--success)' }}>{overall.won}</span>
          <span className="crm-stat-label">{t('crmWonDeals')}</span>
        </div>
        <div className="crm-stat-item">
          <span className="crm-stat-value" style={{ color: 'var(--error)' }}>{overall.lost}</span>
          <span className="crm-stat-label">{t('crmLostLabel') || 'Lost'}</span>
        </div>
        <div className="crm-stat-item">
          <span className="crm-stat-value" style={{ color: 'var(--warning)' }}>{overall.unassigned}</span>
          <span className="crm-stat-label">{t('crmUnassigned') || 'Unassigned'}</span>
        </div>
        <div className="crm-stat-item">
          <span className="crm-stat-value" style={{ color: 'var(--success)' }}>{overall.potentialRate.toFixed(1)}%</span>
          <span className="crm-stat-label">{t('crmPotentialRate') || 'Potential Rate'}</span>
        </div>
      </div>

      {/* Sales Performance Table */}
      <div className="card mb-6">
        <div className="card-header"><h2>{t('crmSalesPerformance') || 'Sales Performance'}</h2></div>
        <div className="data-table-wrapper">
          <table className="data-table" style={{ fontSize: '0.82rem' }}>
            <thead>
              <tr>
                <th>{t('crmAssignedTo')}</th>
                <th>{t('crmTotalLeads')}</th>
                <th style={{ color: SCOLORS.interested }}>{t('crmPotential') || 'Potential'}</th>
                <th style={{ color: SCOLORS.won }}>{t('crmWonDeals')}</th>
                <th style={{ color: SCOLORS.lost }}>{t('crmLostLabel') || 'Lost'}</th>
                <th>{t('crmConversionRate')}</th>
                <th>{t('crmAvgResponse') || 'Avg Response'}</th>
                <th>{t('crmContactedCount') || 'Contacted'}</th>
                <th>{t('crmNewLeads')}</th>
              </tr>
            </thead>
            <tbody>
              {salesPerformance.map((s, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{s.name}</td>
                  <td style={{ fontWeight: 600 }}>{s.total}</td>
                  <td style={{ color: '#8b5cf6', fontWeight: 600 }}>{s.potential}</td>
                  <td style={{ color: 'var(--success)', fontWeight: 700 }}>{s.won}</td>
                  <td style={{ color: 'var(--error)' }}>{s.lost}</td>
                  <td style={{ color: s.conversionRate > 5 ? 'var(--success)' : 'var(--text-secondary)' }}>{s.conversionRate.toFixed(1)}%</td>
                  <td style={{ color: s.avgResponseHours != null && s.avgResponseHours < 24 ? 'var(--success)' : s.avgResponseHours != null ? 'var(--warning)' : 'var(--text-secondary)' }}>
                    {fmtH(s.avgResponseHours)}
                  </td>
                  <td>{s.contacted}</td>
                  <td style={{ color: SCOLORS.new }}>{s.new}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-2 mb-6">
        {/* Pipeline Chart */}
        <div className="card">
          <div className="card-header"><h2>{t('crmPipeline') || 'Pipeline'}</h2></div>
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pipeline.filter(p => p.count > 0)} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis type="number" stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
                <YAxis type="category" dataKey="status" width={120} stroke="var(--text-secondary)"
                  tick={{ fill: 'var(--text-secondary)', fontSize: 10 }}
                  tickFormatter={s => t('crmStatus_' + s)} />
                <Tooltip contentStyle={tooltipStyle} labelFormatter={s => t('crmStatus_' + s)} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]} name={t('crmTotalLeads')}>
                  {pipeline.filter(p => p.count > 0).map((p, i) => (
                    <rect key={i} fill={SCOLORS[p.status] || '#888'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Source Breakdown */}
        <div className="card">
          <div className="card-header"><h2>{t('crmBySource') || 'By Source'}</h2></div>
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bySource.map(s => ({ ...s, source: sourceLabel(s.source, lang) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="source" stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                <YAxis stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                <Bar dataKey="total" fill="var(--accent)" name={t('crmTotalLeads')} radius={[4, 4, 0, 0]} />
                <Bar dataKey="potential" fill="#8b5cf6" name={t('crmPotential') || 'Potential'} radius={[4, 4, 0, 0]} />
                <Bar dataKey="won" fill="var(--success)" name={t('crmWonDeals')} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Per-member bar chart */}
      {salesPerformance.length > 0 && (
        <div className="card mb-6">
          <div className="card-header"><h2>{t('crmMemberComparison') || 'Team Comparison'}</h2></div>
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={salesPerformance}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                <YAxis stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                <Bar dataKey="total" fill="var(--accent)" name={t('crmTotalLeads')} radius={[4, 4, 0, 0]} />
                <Bar dataKey="potential" fill="#8b5cf6" name={t('crmPotential') || 'Potential'} radius={[4, 4, 0, 0]} />
                <Bar dataKey="won" fill="var(--success)" name={t('crmWonDeals')} radius={[4, 4, 0, 0]} />
                <Bar dataKey="lost" fill="var(--error)" name={t('crmLostLabel') || 'Lost'} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ──

function Leads() {
  const { t, lang } = useLanguage();
  const { user, can } = useAuth();
  const brand = useBrand();
  // Projects for the lead form come from the tenant's config list AND its actual inventory
  // (الوحدات) — a tenant that defines projects only by adding units (e.g. Mawreth, whose
  // PROJECTS env is empty) still gets a populated dropdown. Union, de-duplicated, config first.
  const [unitProjects, setUnitProjects] = useState([]);
  useEffect(() => { leadProjectsList().then(r => setUnitProjects(r.projects || [])); }, []);
  PROJECTS = [...new Set([...(brand.projects || []), ...unitProjects])];
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const isBroker = user?.role === 'broker';
  const isBrokerOwner = isBroker && !!user?.broker_owner;
  const role = isAdmin ? 'admin' : 'sales';
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [salesTeam, setSalesTeam] = useState([]);
  const [view, setView] = useState('table');
  const location = useLocation();
  const initialProject = useMemo(() => new URLSearchParams(location.search).get('project') || '', [location.search]);
  // Deep-link support: /leads?source=broker&broker=<company> (from the broker monitor)
  const initialSource = useMemo(() => new URLSearchParams(location.search).get('source') || '', [location.search]);
  const initialBroker = useMemo(() => new URLSearchParams(location.search).get('broker') || '', [location.search]);
  const [filters, setFilters] = useState({ source: initialSource, campaigns: [], status: '', assigned: '', broker: initialBroker, dateFrom: '', dateTo: '', createdFrom: '', createdTo: '', search: '', workedOn: '', project: initialProject, important: '' });
  // Sync filter when URL query param changes (e.g., user navigates from Units page)
  useEffect(() => {
    if (initialProject) setFilters(p => ({ ...p, project: initialProject }));
  }, [initialProject]);
  useEffect(() => {
    if (initialSource || initialBroker) setFilters(p => ({ ...p, source: initialSource || p.source, broker: initialBroker || p.broker }));
  }, [initialSource, initialBroker]);
  const [brokerSummary, setBrokerSummary] = useState([]);
  const [showBrokerPanel, setShowBrokerPanel] = useState(false);
  const brokerCompanies = useMemo(() => brokerSummary.map(b => b.company), [brokerSummary]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showBrokerAdd, setShowBrokerAdd] = useState(false);
  const [dupInfo, setDupInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkAssignTo, setBulkAssignTo] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [page, setPage] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const PAGE_SIZE = 50;

  // Keep the placeholder in sync
  SALES_TEAM_PLACEHOLDER = salesTeam;

  // Debounce search to avoid firing on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search || ''), 400);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Reset page when filters change
  const [prevFilterKey, setPrevFilterKey] = useState('');
  useEffect(() => {
    const key = JSON.stringify({ ...filters, search: debouncedSearch });
    if (key !== prevFilterKey) { setPage(0); setPrevFilterKey(key); }
  }, [filters, debouncedSearch, prevFilterKey]);

  // Fetch sales team and stats once on mount (not on every filter change)
  useEffect(() => {
    (async () => {
      // Brokers use their own scoped team endpoint — the admin users list and the
      // broker summary are admin-only (they'd 403), so skip them for brokers.
      if (isBroker) {
        const teamRes = await fetchSalesTeam();
        if (teamRes.team?.length) setSalesTeam(teamRes.team);
        return;
      }
      try {
        const usersRes = await axios.get('/api/auth/users');
        if (usersRes.data.users?.length) {
          // Brokers run their own scoped teams; the DEFAULT/system admin ('Admin') must
          // never be an assignee. Real admin-people stay assignable.
          setSalesTeam(usersRes.data.users.filter(u => u.role !== 'broker' && u.name !== 'Admin').map(u => u.name));
        }
      } catch {
        const teamRes = await fetchSalesTeam();
        if (teamRes.team?.length) setSalesTeam(teamRes.team);
      }
    })();
    fetchLeadStats().then(s => setStats(s));
    // Broker companies for the broker filter (admin/manager only; skip for brokers).
    if (!isBroker) {
      axios.get('/api/leads/broker-summary').then(r => setBrokerSummary(r.data.brokers || [])).catch(() => {});
    }
  }, [isBroker]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const params = { ...filters, search: debouncedSearch, date_from: filters.dateFrom, date_to: filters.dateTo, created_from: filters.createdFrom, created_to: filters.createdTo, worked_on: filters.workedOn, page, limit: PAGE_SIZE };
      // Send campaigns as comma-separated string for multi-select
      if (filters.campaigns?.length > 0) {
        params.campaign = filters.campaigns.join(',');
      }
      delete params.campaigns;
      // Server enforces sales filtering via JWT — no need to send role/name params

      const leadsRes = await fetchLeads(params);
      setLeads(leadsRes.leads || []);
      setTotalLeads(leadsRes.total || leadsRes.leads?.length || 0);
    } catch (err) {
      // 401 is handled globally (logout). Any other failure (network / 5xx during
      // a deploy restart) shows a retry state rather than a blank/empty table.
      console.error('[Leads] loadData error:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [filters, debouncedSearch, page]);

  useEffect(() => { loadData(); }, [loadData]);

  // Star / un-star a lead as high-potential. Optimistic — the star flips instantly;
  // a failure reloads to revert. (The server re-sorts important-first on the next load.)
  const handleToggleImportant = useCallback(async (lead) => {
    const next = lead.isImportant ? 0 : 1;
    setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, isImportant: next } : l));
    const res = await toggleLeadImportant(lead.id, next);
    if (res?.error) loadData();
  }, [loadData]);

  const [allCampaigns, setAllCampaigns] = useState([]);
  // Fetch campaign list from server once
  useEffect(() => {
    axios.get('/api/leads/campaigns').then(res => {
      if (res.data.campaigns?.length) setAllCampaigns(res.data.campaigns);
    }).catch(() => {});
  }, []);
  const campaigns = allCampaigns;

  const handleAddLead = useCallback(async (data) => {
    const result = await createLead(data);
    if (result?.error === 'duplicate_phone') {
      setDupInfo({ duplicate: result.duplicate, history: result.history });
      return;
    }
    if (result?.error) {
      alert(result.message || result.error);
      return;
    }
    setShowAdd(false);
    loadData();
  }, [loadData]);

  const handleUpdateLead = useCallback(async (data) => {
    await apiUpdateLead(data.id, data);
    setSelectedLead(null);
    loadData();
  }, [loadData]);

  // Broker owner reassigns a lead to one of their own employees (scoped endpoint).
  const handleBrokerAssign = useCallback(async (leadId, assignedTo) => {
    const res = await brokerAssignLead(leadId, assignedTo);
    if (res?.error) return res;
    setSelectedLead(null);
    loadData();
    return res;
  }, [loadData]);

  const handleDeleteLead = useCallback(async (id) => {
    await apiDeleteLead(id);
    setSelectedLead(null);
    loadData();
  }, [loadData]);

  const handleMoveStatus = useCallback(async (id, newStatus) => {
    // For any status change via drag, open the lead modal so user uses the 3 outcome cards
    const lead = leads.find(l => l.id === id);
    if (lead && lead.status !== 'won' && lead.status !== 'lost') {
      setSelectedLead(lead);
      return;
    }
    await patchLeadStatus(id, newStatus);
    loadData();
  }, [loadData, leads]);

  const handleTransition = useCallback(async (leadId, payload) => {
    // Close modal immediately for snappy UX
    setSelectedLead(null);
    // Optimistic: update the lead status in local state instantly
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: payload.toStatus, followUpDate: payload.followUpDate || l.followUpDate, project: payload.project || l.project } : l));
    let res = await transitionLeadStatus(leadId, payload);
    if (res?.error === 'daily_limit_exceeded') {
      const msg = lang === 'ar'
        ? `هذا اليوم (${res.date}) فيه بالفعل ${res.count} متابعة/زيارة، والحد المسموح ${res.limit} في اليوم. هل تريد الجدولة على أي حال؟`
        : `This day (${res.date}) already has ${res.count} follow-ups/visits (limit ${res.limit}/day). Schedule anyway?`;
      if (window.confirm(msg)) {
        res = await transitionLeadStatus(leadId, { ...payload, bypassLimit: true });
      } else {
        loadData(); // restore the optimistically-updated lead
        return;
      }
    }
    if (res?.error === 'already_closed') {
      window.alert(lang === 'ar'
        ? 'هذا العميل مغلق مسبقاً. لإعادة العمل عليه، استخدم «إعادة فتح».'
        : 'This lead is already closed. Use "Reopen" to work on it again.');
      loadData();
      return;
    }
    if (res?.error) { loadData(); return; }
    fetchLeadStats().then(s => setStats(s));
  }, [lang, loadData]);

  const handleReopen = useCallback(async (leadId) => {
    if (!window.confirm(lang === 'ar'
      ? 'إعادة فتح هذا العميل؟ سيعود إلى قائمة المتابعة بحالة «تم التواصل».'
      : 'Reopen this lead? It returns to active follow-ups as "Contacted".')) return;
    setSelectedLead(null);
    const res = await reopenLead(leadId);
    if (res?.error) { loadData(); return; }
    loadData();
    fetchLeadStats().then(s => setStats(s));
  }, [lang, loadData]);

  const handleAddComm = useCallback(async (leadId, entry) => {
    const result = await addCommunication(leadId, entry);
    loadData();
    return result;
  }, [loadData]);

  const handleDirective = useCallback(async (leadId, message, assignedTo) => {
    const res = await sendDirective(leadId, message, assignedTo);
    if (!res?.error) { loadData(); fetchLeadStats().then(s => setStats(s)); }
    return res;
  }, [loadData]);

  const handleReply = useCallback(async (leadId, message) => {
    const res = await replyDirective(leadId, message);
    if (!res?.error) loadData();
    return res;
  }, [loadData]);

  const handleToggleSelect = useCallback((id) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((ids, checked) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => checked ? next.add(id) : next.delete(id));
      return next;
    });
  }, []);

  const handleBulkAssign = useCallback(async () => {
    if (!bulkAssignTo || bulkSelected.size === 0) return;
    // Guard against an accidental "select all" → mass reassignment.
    const confirmMsg = lang === 'ar'
      ? `إعادة تعيين ${bulkSelected.size} عميل إلى ${bulkAssignTo}؟`
      : `Reassign ${bulkSelected.size} lead(s) to ${bulkAssignTo}?`;
    if (!window.confirm(confirmMsg)) return;
    setBulkAssigning(true);
    try {
      await axios.post('/api/leads/bulk-assign', { leadIds: [...bulkSelected], assignedTo: bulkAssignTo });
      setBulkSelected(new Set());
      setBulkAssignTo('');
      loadData();
    } catch {}
    setBulkAssigning(false);
  }, [bulkAssignTo, bulkSelected, loadData, lang]);

  const [syncMsg, setSyncMsg] = useState(null);

  const handleSyncLeads = useCallback(async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await axios.post('/api/leads/sync-sheet');
      // The endpoint returns one result per source: {snapchat, tiktok, extra, riva}.
      // This used to read res.data.snapchat and then look for .snapchat/.tiktok inside
      // it, so the totals were always 0 and Riva was never counted at all.
      const d = res.data || {};
      const sources = [d.snapchat, d.tiktok, d.extra, d.riva].filter(Boolean);
      const imp = sources.reduce((n, s) => n + (s.imported || 0), 0);
      const dup = sources.reduce((n, s) => n + (s.duplicate || 0), 0);
      // A source that couldn't read its sheet must not hide behind "no new leads".
      const broken = d.riva?.snapError || (d.riva && d.riva.enabled === false ? d.riva.reason : null);
      if (broken) {
        setSyncMsg({ type: 'error', text: `Riva: ${broken}` });
      } else if (imp > 0) {
        setSyncMsg({ type: 'success', text: `${imp} new leads imported` });
      } else {
        setSyncMsg({ type: 'info', text: `No new leads (${dup} already exist)` });
      }
      loadData();
    } catch {
      setSyncMsg({ type: 'error', text: 'Sync failed' });
    }
    setSyncing(false);
    setTimeout(() => setSyncMsg(null), 4000);
  }, [loadData]);

  const [exporting, setExporting] = useState(false);
  // Export the current filtered view to a CSV (one row per client + status track
  // record). Mirrors the exact params the leads list sends so it exports what's
  // on screen. Server streams the file; auth rides the global axios header.
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = { ...filters, search: debouncedSearch, date_from: filters.dateFrom, date_to: filters.dateTo, created_from: filters.createdFrom, created_to: filters.createdTo, worked_on: filters.workedOn };
      if (filters.campaigns?.length > 0) params.campaign = filters.campaigns.join(',');
      delete params.campaigns; delete params.dateFrom; delete params.dateTo; delete params.createdFrom; delete params.createdTo; delete params.workedOn;
      const res = await axios.get('/api/leads/export', { params, responseType: 'blob' });
      // Use the server-provided filename (xlsx normally; csv if a tenant lacks exceljs).
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `yamam-leads-${new Date().toISOString().slice(0, 10)}.xlsx`;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setSyncMsg({ type: 'error', text: lang === 'ar' ? 'فشل التصدير' : 'Export failed' });
      setTimeout(() => setSyncMsg(null), 4000);
    }
    setExporting(false);
  }, [filters, debouncedSearch, lang]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="badge" style={{ background: isAdmin ? 'rgba(99,102,241,0.15)' : 'rgba(34,197,94,0.15)', color: isAdmin ? 'var(--accent)' : 'var(--success)', fontSize: '0.78rem' }}>
            {isAdmin ? t('crmAdmin') : t('crmSales')}: {user?.name}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="tabs">
            <button className={`tab ${view === 'table' ? 'active' : ''}`} onClick={() => setView('table')}>{t('crmTableView')}</button>
            <button className={`tab ${view === 'kanban' ? 'active' : ''}`} onClick={() => setView('kanban')}>{t('crmKanbanView')}</button>
          </div>
          {isAdmin && (
            <button className="btn btn-secondary btn-sm" onClick={handleSyncLeads} disabled={syncing} title="Fetch latest leads from Snapchat & TikTok sheets">
              {syncing ? '...' : '↻ Fetch Leads'}
            </button>
          )}
          {isAdmin && brokerSummary.length > 0 && (
            <button className={`btn btn-sm ${showBrokerPanel ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setShowBrokerPanel(v => !v)} title={lang === 'ar' ? 'أداء الوسطاء' : 'Broker performance'}>
              {lang === 'ar' ? 'الوسطاء' : 'Brokers'} ({brokerSummary.length})
            </button>
          )}
          {isAdmin && (
            <button className="btn btn-secondary btn-sm" onClick={handleExport} disabled={exporting} title={lang === 'ar' ? 'تصدير العملاء إلى Excel مع سجل الحالة (يتبع الفلاتر الحالية)' : 'Export clients to Excel with status track record (respects current filters)'}>
              {exporting ? '...' : (lang === 'ar' ? '⬇ تصدير Excel' : '⬇ Export Excel')}
            </button>
          )}
          {can('leads.create') && (
            <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ {t('crmAddLead')}</button>
          )}
          {isAdmin && can('leads.create') && (
            <BulkImportButton lang={lang} onDone={loadData} />
          )}
          {isBroker && (
            <button className="btn btn-primary" onClick={() => setShowBrokerAdd(true)}>+ {lang === 'ar' ? 'إضافة عميل' : 'Add customer'}</button>
          )}
        </div>
      </div>

      {syncMsg && (
        <div style={{
          padding: '10px 16px', borderRadius: 10, marginBottom: 16, fontSize: '0.85rem', fontWeight: 500,
          background: syncMsg.type === 'success' ? 'rgba(34,197,94,0.1)' : syncMsg.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(99,102,241,0.1)',
          border: `1px solid ${syncMsg.type === 'success' ? 'rgba(34,197,94,0.3)' : syncMsg.type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(99,102,241,0.3)'}`,
          color: syncMsg.type === 'success' ? 'var(--success)' : syncMsg.type === 'error' ? 'var(--error)' : 'var(--accent)',
        }}>{syncMsg.text}</div>
      )}

      <LeadStats stats={stats} t={t} />
      {isAdmin && showBrokerPanel && (
        <BrokerMonitor brokers={brokerSummary} lang={lang}
          onPick={(company) => { setFilters(p => ({ ...p, source: 'broker', broker: company })); setShowBrokerPanel(false); setPage(0); }} />
      )}
      <FilterBar filters={filters} setFilters={setFilters} campaigns={campaigns} salesTeam={salesTeam} brokerCompanies={brokerCompanies} isAdmin={isAdmin} t={t} lang={lang} />

      {can('leads.reassign') && bulkSelected.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
          background: 'var(--bg-card)', border: '1px solid var(--accent)', borderRadius: 12,
          marginBottom: 12, flexWrap: 'wrap',
        }}>
          <span style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--accent)' }}>
            {bulkSelected.size} {t('crmSelected')}
          </span>
          <select value={bulkAssignTo} onChange={e => setBulkAssignTo(e.target.value)}
            style={{ width: 'auto', padding: '6px 12px', fontSize: '0.82rem' }}>
            <option value="">{t('crmAssignTo')}</option>
            {salesTeam.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="btn btn-primary btn-sm" onClick={handleBulkAssign}
            disabled={!bulkAssignTo || bulkAssigning}>
            {bulkAssigning ? '...' : t('crmAssignBtn')}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setBulkSelected(new Set())}>
            {t('crmClearSelection')}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: '2rem', marginBottom: 12, opacity: 0.4 }}>&#9203;</div>
          <div style={{ color: 'var(--text-secondary)' }}>{t('loading')}</div>
        </div>
      ) : error ? (
        <LoadError onRetry={loadData} />
      ) : view === 'kanban' ? (
        <KanbanBoard leads={leads} onMoveStatus={handleMoveStatus} onLeadClick={l => setSelectedLead(l)} t={t}
          total={totalLeads} page={page} onPageChange={setPage} pageSize={PAGE_SIZE} />
      ) : (
        <TableView leads={leads} onLeadClick={l => setSelectedLead(l)} canSelect={isAdmin || can('leads.reassign')}
          selectedIds={bulkSelected} onToggleSelect={handleToggleSelect} onSelectAll={handleSelectAll}
          onToggleImportant={user?.role !== 'viewer' ? handleToggleImportant : null} t={t}
          total={totalLeads} page={page} onPageChange={setPage} pageSize={PAGE_SIZE} />
      )}

      {selectedLead && (
        <LeadModal
          lead={leads.find(l => l.id === selectedLead.id) || selectedLead}
          onClose={() => setSelectedLead(null)}
          onSave={handleUpdateLead}
          onDelete={handleDeleteLead}
          onAddComm={handleAddComm}
          onTransition={handleTransition}
          onReopen={handleReopen}
          onDirective={handleDirective}
          onReply={handleReply}
          onBrokerAssign={handleBrokerAssign}
          onToggleImportant={user?.role !== 'viewer' ? handleToggleImportant : null}
          isBrokerOwner={isBrokerOwner}
          salesTeam={salesTeam}
          brokerCompanies={brokerCompanies}
          role={role} t={t} lang={lang}
        />
      )}
      {showAdd && <AddLeadModal onClose={() => setShowAdd(false)} onSave={handleAddLead} role={role} salesTeam={salesTeam} brokerCompanies={brokerCompanies} userName={user?.name} t={t} lang={lang} />}
      {showBrokerAdd && <BrokerAddCustomerModal onClose={() => setShowBrokerAdd(false)} onAdded={loadData} isBrokerOwner={isBrokerOwner} salesTeam={salesTeam} t={t} lang={lang} isRTL={lang === 'ar'} />}
      {dupInfo && (() => {
        const d = dupInfo.duplicate || {};
        const comms = dupInfo.history?.communications || [];
        const trans = dupInfo.history?.transitions || [];
        const fmtDate = (dt) => dt ? new Date(dt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setDupInfo(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, width: '90%', maxWidth: 500, maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: '1.4rem' }}>{'\u26A0\uFE0F'}</span>
                <h3 style={{ margin: 0, color: '#ef4444', fontSize: '1rem' }}>{t('duplicatePhoneError') || 'Duplicate Phone Number'}</h3>
              </div>
              <div style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: 4 }}>{d.name}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  {t('crmAssignedTo')}: <strong>{d.assignedTo || '—'}</strong> &nbsp;|&nbsp; {t('crmStatus')}: <strong>{t('crmStatus_' + d.status) || d.status}</strong>
                </div>
              </div>
              {trans.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 6 }}>{t('crmTransitions') || 'Status History'}</div>
                  {trans.map((tr, i) => (
                    <div key={i} style={{ fontSize: '0.75rem', padding: '6px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span><strong>{tr.done_by}</strong>: {t('crmStatus_' + tr.from_status) || tr.from_status} {'\u2192'} {t('crmStatus_' + tr.to_status) || tr.to_status}</span>
                      <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{fmtDate(tr.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
              {comms.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 6 }}>{t('crmCommunications') || 'Communications'}</div>
                  {comms.map((c, i) => (
                    <div key={i} style={{ fontSize: '0.75rem', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span><strong>{c.created_by}</strong> ({c.type})</span>
                        <span style={{ color: 'var(--text-secondary)' }}>{fmtDate(c.created_at)}</span>
                      </div>
                      {c.notes && (() => { const m = c.notes.replace(/\n?<!--meta:[\s\S]*?-->/g, '').trim(); return m ? <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>{m}</div> : null; })()}
                    </div>
                  ))}
                </div>
              )}
              {trans.length === 0 && comms.length === 0 && (
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 14 }}>{t('noHistory') || 'No communication history yet'}</div>
              )}
              <button onClick={() => setDupInfo(null)} style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}>
                {t('close') || 'Close'}
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default Leads;
